import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Users,
  Layers,
  Settings,
  Search,
  ChevronDown,
  CheckCircle,
  AlertTriangle,
  Info,
  ChevronUp,
  ChevronsUpDown,
  Copy,
  UserPlus,
  Briefcase,
  ClipboardCopy,
  ClipboardPaste,
  GitMerge,
  Replace,
  Archive,
  CopyPlus,
  Calendar,
  MessageSquare,
} from "lucide-react";

const GroupeDaluxTooltip = ({ groupeName, children, contactsData }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [personnes, setPersonnes] = useState([]);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const getPersonnesFromGroupe = (groupeDaluxName) => {
    if (
      !contactsData ||
      !contactsData.objects ||
      contactsData.objects.length === 0
    ) {
      return [];
    }

    const personnes = [];
    contactsData.objects.forEach((contact) => {
      const nom = contact["Nom"] || contact["nom"] || "";
      const groupes = contact["Groupes"] || contact["groupes"] || "";

      if (
        groupes &&
        typeof groupes === "string" &&
        groupes.includes(groupeDaluxName)
      ) {
        if (nom && nom.trim() !== "") {
          personnes.push(nom.trim());
        }
      }
    });
    return personnes;
  };

  const handleMouseEnter = (e) => {
    const rect = e.target.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });

    const foundPersonnes = getPersonnesFromGroupe(groupeName);
    setPersonnes(foundPersonnes);
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: "help" }}
      >
        {children}
      </span>

      {isVisible && personnes.length > 0 && (
        <div
          className="fixed z-50 bg-gray-800 text-white p-3 rounded-lg shadow-lg max-w-xs"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} className="text-purple-400" />
            <span className="font-medium text-purple-300">Personnes</span>
          </div>
          <div className="space-y-1 text-sm">
            {personnes.slice(0, 10).map((personne, idx) => (
              <div key={idx} className="text-gray-200">
                {personne}
              </div>
            ))}
            {personnes.length > 10 && (
              <div className="text-gray-400 italic">
                ... et {personnes.length - 10} autres
              </div>
            )}
          </div>
          <div className="text-xs text-gray-400 mt-2">
            {personnes.length} personne{personnes.length > 1 ? "s" : ""}
          </div>
        </div>
      )}
    </>
  );
};

const IndividuelTooltip = ({ personnes, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e) => {
    const rect = e.target.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
    setIsVisible(true);
  };

  const handleMouseLeave = (e) => {
    setIsVisible(false);
  };

  return (
    <>
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: "help" }}
      >
        {children}
      </span>

      {isVisible && personnes && personnes.length > 0 && (
        <div
          className="fixed z-50 bg-gray-800 text-white p-3 rounded-lg shadow-lg max-w-xs"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} className="text-green-400" />
            <span className="font-medium text-green-300">
              Personnes Individuelles
            </span>
          </div>
          <div className="space-y-1 text-sm">
            {personnes.slice(0, 10).map((personne, idx) => (
              <div key={idx} className="text-gray-200">
                {personne}
              </div>
            ))}
            {personnes.length > 10 && (
              <div className="text-gray-400 italic">
                ... et {personnes.length - 10} autres
              </div>
            )}
          </div>
          <div className="text-xs text-gray-400 mt-2">
            {personnes.length} personne{personnes.length > 1 ? "s" : ""}
          </div>
        </div>
      )}
    </>
  );
};

const ObservateurTooltip = ({ groupes, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e) => {
    const rect = e.target.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
    setIsVisible(true);
  };

  const handleMouseLeave = (e) => {
    setIsVisible(false);
  };

  return (
    <>
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: "help" }}
      >
        {children}
      </span>

      {isVisible && groupes && groupes.length > 0 && (
        <div
          className="fixed z-50 bg-gray-800 text-white p-3 rounded-lg shadow-lg max-w-xs"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} className="text-gray-400" />
            <span className="font-medium text-gray-300">
              Groupes Observateurs
            </span>
          </div>
          <div className="space-y-1 text-sm">
            {groupes.map((groupe, idx) => (
              <div key={idx} className="text-gray-200">
                {groupe}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const WarningTooltip = ({ text, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e) => {
    const rect = e.target.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <span onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </span>

      {isVisible && (
        <div
          className="fixed z-50 bg-yellow-50 text-yellow-800 p-3 rounded-lg shadow-lg max-w-xs border border-yellow-200 text-xs"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
          }}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle
              size={16}
              className="text-yellow-500 flex-shrink-0 mt-0.5"
            />
            <span>{text}</span>
          </div>
        </div>
      )}
    </>
  );
};

const CommentDisplayTooltip = ({ comment, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  if (!comment) {
    return <>{children}</>;
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className="absolute z-30 w-64 p-2 text-xs text-white bg-gray-800 rounded-md shadow-lg bottom-full left-1/2 -translate-x-1/2 mb-2 break-words">
          {comment}
        </div>
      )}
    </div>
  );
};

const CommentEditor = ({ initialComment, onSave, onCancel, position }) => {
  const [comment, setComment] = useState(initialComment || "");
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onCancel();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onCancel]);

  const handleDelete = () => {
    onSave(""); // Sauvegarder un commentaire vide équivaut à le supprimer
  };

  return (
    <div
      ref={modalRef}
      className="fixed z-30 bg-white p-4 rounded-lg shadow-xl border w-80"
      style={{ top: position.y, left: position.x }}
    >
      <h3 className="text-sm font-medium text-gray-800 mb-2">
        Éditer le commentaire
      </h3>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        className="w-full border-gray-300 rounded-md text-xs p-2 focus:ring-blue-500 focus:border-blue-500"
        rows="4"
        autoFocus
      />
      <div className="flex justify-between items-center mt-3">
        <button
          onClick={handleDelete}
          className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
        >
          Supprimer
        </button>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Annuler
          </button>
          <button
            onClick={() => onSave(comment)}
            className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Sauver
          </button>
        </div>
      </div>
    </div>
  );
};

const GroupeFluxTooltip = ({ group, children, contactsData }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const getPersonnesFromGroupe = (groupeDaluxName) => {
    if (
      !contactsData ||
      !contactsData.objects ||
      contactsData.objects.length === 0
    ) {
      return [];
    }
    const personnes = [];
    contactsData.objects.forEach((contact) => {
      const nom = contact["Nom"] || contact["nom"] || "";
      const groupes = contact["Groupes"] || contact["groupes"] || "";
      if (
        groupes &&
        typeof groupes === "string" &&
        groupes.includes(groupeDaluxName)
      ) {
        if (nom && nom.trim() !== "") {
          personnes.push(nom.trim());
        }
      }
    });
    return personnes;
  };

  const handleMouseEnter = (e) => {
    const rect = e.target.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const hasContent =
    (group.groupeDalux && group.groupeDalux.length > 0) ||
    (group.individuelPersons && group.individuelPersons.length > 0);

  return (
    <>
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: "help" }}
      >
        {children}
      </span>

      {isVisible && hasContent && (
        <div
          className="fixed z-50 bg-gray-800 text-white p-3 rounded-lg shadow-lg max-w-xs"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
          }}
        >
          <div className="flex items-center gap-2 mb-2 border-b border-gray-600 pb-2">
            <Briefcase size={16} className="text-red-400" />
            <span className="font-medium text-red-300">{group.groupeFlux}</span>
          </div>
          <div className="space-y-2 text-sm">
            {group.groupeDalux &&
              group.groupeDalux.length > 0 &&
              group.groupeDalux.map((dalux, idx) => {
                const members = getPersonnesFromGroupe(dalux);
                return (
                  <div key={idx}>
                    <div className="font-semibold text-purple-300 text-xs flex items-center gap-1.5">
                      <Users size={12} /> {dalux}
                    </div>
                    {members.length > 0 ? (
                      <ul className="list-disc list-inside pl-2 text-gray-300 text-xs">
                        {members.map((member, mIdx) => (
                          <li key={mIdx}>{member}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-gray-400 text-xs pl-2 italic">
                        Aucune personne
                      </div>
                    )}
                  </div>
                );
              })}
            {group.individuelPersons && group.individuelPersons.length > 0 && (
              <div>
                <div className="font-semibold text-green-300 text-xs flex items-center gap-1.5">
                  <UserPlus size={12} /> Personnes individuelles
                </div>
                <ul className="list-disc list-inside pl-2 text-gray-300 text-xs">
                  {group.individuelPersons.map((person, pIdx) => (
                    <li key={pIdx}>{person}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  className,
  allowCreate = false,
  onCreate,
  variant = "default",
  startOpen = false,
  onClose = () => {},
}) => {
  const [isOpen, setIsOpen] = useState(startOpen);
  const [searchTerm, setSearchTerm] = useState("");
  const selectRef = useRef(null);
  const inputRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.filter(
      (option) =>
        option &&
        typeof option === "string" &&
        option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchTerm("");
    onClose();
  };

  const handleSelect = (option) => {
    onChange(option);
    closeDropdown();
  };

  const handleCreate = () => {
    const newItem = searchTerm.trim();
    if (
      onCreate &&
      newItem &&
      !options.some((o) => o.toLowerCase() === newItem.toLowerCase())
    ) {
      onCreate(newItem);
      onChange(newItem);
    }
    closeDropdown();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (startOpen) {
      setIsOpen(true);
    }
  }, [startOpen]);

  const renderOptions = () => (
    <>
      {filteredOptions.map((option, index) => (
        <li
          key={index}
          className="text-gray-900 cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50"
          onClick={() => handleSelect(option)}
        >
          <span className="font-normal block truncate">{option}</span>
        </li>
      ))}
      {allowCreate &&
        searchTerm.trim() &&
        !filteredOptions.some(
          (o) => o.toLowerCase() === searchTerm.trim().toLowerCase()
        ) && (
          <li
            className="text-green-600 cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-green-50"
            onClick={handleCreate}
          >
            <span className="font-semibold block truncate">
              <Plus size={14} className="inline mr-2" />
              Créer "{searchTerm.trim()}"
            </span>
          </li>
        )}
      {filteredOptions.length === 0 && (!allowCreate || !searchTerm.trim()) && (
        <li className="text-gray-500 cursor-default select-none relative py-2 px-4">
          Aucun résultat
        </li>
      )}
    </>
  );

  if (variant === "button") {
    return (
      <div className={`relative ${className}`} ref={selectRef}>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="w-full text-left text-xs font-medium text-gray-500 bg-gray-50 hover:bg-gray-100 border border-dashed border-gray-300 rounded-md px-2 py-1.5 flex items-center gap-1.5 transition-colors"
        >
          <Plus size={12} className="text-gray-400" />
          <span>{placeholder}</span>
        </button>
        {isOpen && (
          <ul className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            <li className="p-2 sticky top-0 bg-white z-10 border-b border-gray-200">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  ref={inputRef}
                  type="text"
                  className="w-full border-gray-200 rounded-md p-1.5 pl-8 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </li>
            {renderOptions()}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={selectRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className="w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left text-sm cursor-default focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          placeholder={placeholder}
          value={searchTerm}
          onClick={() => setIsOpen(true)}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <ChevronsUpDown
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </span>
      </div>

      {isOpen && (
        <ul className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {renderOptions()}
        </ul>
      )}
    </div>
  );
};

const EditFluxModal = ({
  flux,
  setEditingFlux,
  availableGroups,
  setAvailableGroups,
  onSave,
  onCancel,
  removeGroupeFlux,
  addGroupeDalux,
  removeGroupeDalux,
  contactsData,
  addToast,
  onPasteConfig,
  hasCopiedConfig,
}) => {
  const [activeAdder, setActiveAdder] = useState(null); // { gIdx: number, type: 'dalux' | 'person' }
  const [activeMainAdder, setActiveMainAdder] = useState(null); // 'initiateur' | 'analyse' | 'autoriser'

  const handleMoveGroupeFlux = (index, direction) => {
    const newAnalyse = [...flux.analyse];
    const item = newAnalyse[index];
    const swapIndex = direction === "up" ? index - 1 : index + 1;

    if (swapIndex < 0 || swapIndex >= newAnalyse.length) {
      return;
    }

    newAnalyse[index] = newAnalyse[swapIndex];
    newAnalyse[swapIndex] = item;

    setEditingFlux({ ...flux, analyse: newAnalyse });
  };

  const handleCreateGroupeFlux = (newGroupName) => {
    if (newGroupName && !availableGroups.groupeFlux.includes(newGroupName)) {
      setAvailableGroups((prev) => ({
        ...prev,
        groupeFlux: [...prev.groupeFlux, newGroupName].sort(),
      }));
    }
  };

  const handleCreateGroupeDalux = (newGroupName) => {
    if (newGroupName && !availableGroups.groupeDalux.includes(newGroupName)) {
      setAvailableGroups((prev) => ({
        ...prev,
        groupeDalux: [...prev.groupeDalux, newGroupName].sort(),
      }));
    }
  };

  const handleAddGroupeFlux = (selectedGroupeFlux) => {
    if (selectedGroupeFlux) {
      const updatedFlux = {
        ...flux,
        analyse: [
          ...flux.analyse,
          {
            groupeFlux: selectedGroupeFlux,
            groupeDalux: [],
            individuelPersons: [],
          },
        ],
      };
      setEditingFlux(updatedFlux);
    }
  };

  const removeInitiateur = (dalux, type) => {
    const updatedInitiateur = { ...flux.initiateur };

    if (type === "standard" && updatedInitiateur.standard) {
      updatedInitiateur.standard = updatedInitiateur.standard.filter(
        (i) => i !== dalux
      );
    } else if (type === "observateur" && updatedInitiateur.observateur) {
      updatedInitiateur.observateur = updatedInitiateur.observateur.filter(
        (i) => i !== dalux
      );
    }

    setEditingFlux({ ...flux, initiateur: updatedInitiateur });
  };

  const removeAutoriser = (dalux) => {
    const updatedFlux = {
      ...flux,
      autoriser: flux.autoriser.filter((a) => a !== dalux),
    };
    setEditingFlux(updatedFlux);
  };

  const addIndividualPerson = (gFluxIndex, personName) => {
    if (!personName) return;

    const nouveauFlux = { ...flux };
    nouveauFlux.analyse = [...flux.analyse];
    const targetGroup = { ...flux.analyse[gFluxIndex] };

    if (!targetGroup.individuelPersons) {
      targetGroup.individuelPersons = [];
    }

    if (!targetGroup.individuelPersons.includes(personName)) {
      targetGroup.individuelPersons = [
        ...targetGroup.individuelPersons,
        personName,
      ];
      nouveauFlux.analyse[gFluxIndex] = targetGroup;
      setEditingFlux(nouveauFlux);
    }
  };

  const removeIndividualPerson = (gFluxIndex, personName) => {
    const nouveauFlux = { ...flux };
    nouveauFlux.analyse = [...flux.analyse];
    const targetGroup = { ...flux.analyse[gFluxIndex] };

    if (targetGroup.individuelPersons) {
      targetGroup.individuelPersons = targetGroup.individuelPersons.filter(
        (p) => p !== personName
      );
      nouveauFlux.analyse[gFluxIndex] = targetGroup;
      setEditingFlux(nouveauFlux);
    }
  };

  const handleFluxTypeChange = (field, value) => {
    const updatedFlux = { ...flux, [field]: value };
    setEditingFlux(updatedFlux);
  };

  const handleNameChange = (e) => {
    setEditingFlux({ ...flux, nom: e.target.value });
  };

  const handleToggleAdder = (gIdx, type) => {
    if (activeAdder && activeAdder.gIdx === gIdx && activeAdder.type === type) {
      setActiveAdder(null); // Close if clicking the same icon again
    } else {
      setActiveAdder({ gIdx, type });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1">
      <div className="bg-white rounded-xl shadow-2xl w-full h-full max-w-none border border-gray-100 flex flex-col">
        <div className="px-6 py-3 border-b border-gray-100 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-4 flex-grow mr-4">
            <div className="flex flex-col flex-grow">
              <p className="text-xs text-gray-500 mb-1">
                Nom du flux & Commentaire
              </p>
              <input
                type="text"
                value={flux.nom}
                onChange={handleNameChange}
                className="text-xl font-bold text-gray-900 bg-transparent border-b-2 border-transparent hover:border-gray-200 focus:border-blue-500 focus:outline-none transition-colors p-0 w-full"
              />
              <textarea
                value={flux.comment || ""}
                onChange={(e) =>
                  setEditingFlux({ ...flux, comment: e.target.value })
                }
                placeholder="Ajouter un commentaire de suivi..."
                className="text-xs text-gray-600 bg-transparent border-t-2 border-transparent hover:border-gray-200 focus:border-blue-500 focus:outline-none transition-colors p-1 w-full mt-1"
                rows="2"
              />
            </div>

            <button
              onClick={onPasteConfig}
              disabled={!hasCopiedConfig}
              className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors text-xs font-medium border border-orange-200 disabled:border-gray-200 flex-shrink-0"
              title={
                hasCopiedConfig
                  ? "Coller la configuration copiée"
                  : "Copiez d'abord une configuration depuis la liste des flux"
              }
            >
              <ClipboardPaste size={14} />
              <span>Coller Config</span>
            </button>
          </div>

          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-50 rounded-full transition-all"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          <div className="space-y-3 w-full flex flex-col h-full">
            {/* Configuration Ultra Compacte */}
            <div className="bg-gray-100 rounded p-2 border">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1">
                  <Settings size={12} className="text-gray-600" />
                  <span className="font-medium text-gray-700">Config</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-600">Type:</span>
                    <button
                      onClick={() => handleFluxTypeChange("type", "Mandataire")}
                      className={`px-2 py-0.5 rounded text-xs ${
                        (flux.type || "Mandataire") === "Mandataire"
                          ? "bg-blue-500 text-white"
                          : "bg-gray-300 text-gray-700"
                      }`}
                    >
                      Mandataire
                    </button>
                    <button
                      onClick={() => handleFluxTypeChange("type", "Entreprise")}
                      className={`px-2 py-0.5 rounded text-xs ${
                        flux.type === "Entreprise"
                          ? "bg-purple-500 text-white"
                          : "bg-gray-300 text-gray-700"
                      }`}
                    >
                      Entreprise
                    </button>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-600">Partage:</span>
                    <button
                      onClick={() =>
                        handleFluxTypeChange("typePartage", "Publié")
                      }
                      className={`px-2 py-0.5 rounded text-xs ${
                        (flux.typePartage || "Publié") === "Publié"
                          ? "bg-green-500 text-white"
                          : "bg-gray-300 text-gray-700"
                      }`}
                    >
                      Publié
                    </button>
                    <button
                      onClick={() =>
                        handleFluxTypeChange("typePartage", "Partagé")
                      }
                      className={`px-2 py-0.5 rounded text-xs ${
                        flux.typePartage === "Partagé"
                          ? "bg-yellow-500 text-white"
                          : "bg-gray-300 text-gray-700"
                      }`}
                    >
                      Partagé
                    </button>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-600">Statut:</span>
                    <select
                      value={flux.status || "en attente"}
                      onChange={(e) =>
                        handleFluxTypeChange("status", e.target.value)
                      }
                      className="bg-white border border-gray-300 rounded text-xs px-2 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="en attente">En attente</option>
                      <option value="en cours">En cours</option>
                      <option value="a valider">À valider</option>
                      <option value="valider">Validé</option>
                      <option value="a supprimer">À supprimer</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* LAYOUT EN 3 COLONNES OPTIMISÉ */}
            <div className="flex gap-3 flex-1 min-h-0">
              {/* COLONNE 1 - INITIATEUR (25%) */}
              <div className="w-1/4 bg-blue-50 rounded-lg border-2 border-blue-200 p-3 flex flex-col">
                {/* Section Initiateurs Standard */}
                <div className="flex items-center justify-between gap-2 mb-3 border-b border-blue-300 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center font-bold">
                      1
                    </div>
                    <h3 className="text-xs font-bold text-blue-800">
                      INITIATEUR
                    </h3>
                  </div>
                  <button
                    onClick={() => setActiveMainAdder("initiateur-standard")}
                    className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                    title="Ajouter GroupeDalux"
                  >
                    <Plus size={16} />
                  </button>
                </div>

                {activeMainAdder === "initiateur-standard" && (
                  <div className="mb-3">
                    <SearchableSelect
                      options={availableGroups.groupeDalux}
                      value=""
                      onChange={(selectedValue) => {
                        if (
                          selectedValue &&
                          !flux.initiateur.standard.includes(selectedValue)
                        ) {
                          if (!isFluxGroup(selectedValue)) {
                            addToast(
                              "Attention: Ce groupe n'est peut-être pas destiné à un flux.",
                              "info"
                            );
                          }
                          const updatedFlux = {
                            ...flux,
                            initiateur: {
                              ...flux.initiateur,
                              standard: [
                                ...flux.initiateur.standard,
                                selectedValue,
                              ],
                            },
                          };
                          setEditingFlux(updatedFlux);
                        }
                        setActiveMainAdder(null);
                      }}
                      placeholder="Ajouter GroupeDalux..."
                      className="w-full"
                      allowCreate={true}
                      onCreate={handleCreateGroupeDalux}
                      startOpen={true}
                      onClose={() => setActiveMainAdder(null)}
                    />
                  </div>
                )}

                <div className="flex flex-wrap content-start gap-1 flex-1 overflow-y-auto min-h-[50px]">
                  {flux.initiateur?.standard &&
                  flux.initiateur.standard.length > 0 ? (
                    flux.initiateur.standard.map((dalux, idx) => (
                      <div
                        key={idx}
                        className="bg-blue-600 text-white px-2 py-1 rounded inline-flex items-center justify-between text-xs"
                      >
                        <div className="flex items-center gap-1.5 truncate">
                          {!isFluxGroup(dalux) && (
                            <WarningTooltip text="Ce groupe ne contient pas le mot 'flux'. Il pourrait ne pas être approprié pour un flux de validation.">
                              <AlertTriangle
                                size={12}
                                className="text-yellow-300 flex-shrink-0"
                              />
                            </WarningTooltip>
                          )}
                          <span className="truncate">{dalux}</span>
                        </div>
                        <button
                          onClick={() => removeInitiateur(dalux, "standard")}
                          className="text-blue-200 hover:text-white ml-1 flex-shrink-0"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 text-xs italic text-center py-4">
                      Aucun initiateur
                    </div>
                  )}
                </div>

                {/* Section Observateurs */}
                <div className="flex items-center justify-between gap-2 mt-4 mb-3 border-b border-gray-300 pb-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xs font-bold text-gray-700">
                      OBSERVATEUR
                    </h3>
                  </div>
                  <button
                    onClick={() => setActiveMainAdder("initiateur-observateur")}
                    className="flex items-center justify-center w-6 h-6 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                    title="Ajouter GroupeDalux Observateur"
                  >
                    <Plus size={16} />
                  </button>
                </div>

                {activeMainAdder === "initiateur-observateur" && (
                  <div className="mb-3">
                    <SearchableSelect
                      options={availableGroups.groupeDalux}
                      value=""
                      onChange={(selectedValue) => {
                        if (
                          selectedValue &&
                          !flux.initiateur.observateur.includes(selectedValue)
                        ) {
                          if (!isFluxGroup(selectedValue)) {
                            addToast(
                              "Attention: Ce groupe n'est peut-être pas destiné à un flux.",
                              "info"
                            );
                          }
                          const updatedFlux = {
                            ...flux,
                            initiateur: {
                              ...flux.initiateur,
                              observateur: [
                                ...flux.initiateur.observateur,
                                selectedValue,
                              ],
                            },
                          };
                          setEditingFlux(updatedFlux);
                        }
                        setActiveMainAdder(null);
                      }}
                      placeholder="Ajouter Observateur..."
                      className="w-full"
                      allowCreate={true}
                      onCreate={handleCreateGroupeDalux}
                      startOpen={true}
                      onClose={() => setActiveMainAdder(null)}
                    />
                  </div>
                )}

                <div className="flex flex-wrap content-start gap-1 flex-1 overflow-y-auto min-h-[50px]">
                  {flux.initiateur?.observateur &&
                  flux.initiateur.observateur.length > 0 ? (
                    flux.initiateur.observateur.map((dalux, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-500 text-white px-2 py-1 rounded inline-flex items-center justify-between text-xs"
                      >
                        <div className="flex items-center gap-1.5 truncate">
                          {!isFluxGroup(dalux) && (
                            <WarningTooltip text="Ce groupe ne contient pas le mot 'flux'. Il pourrait ne pas être approprié pour un flux de validation.">
                              <AlertTriangle
                                size={12}
                                className="text-yellow-300 flex-shrink-0"
                              />
                            </WarningTooltip>
                          )}
                          <span className="truncate">{dalux}</span>
                        </div>
                        <button
                          onClick={() => removeInitiateur(dalux, "observateur")}
                          className="text-gray-200 hover:text-white ml-1 flex-shrink-0"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 text-xs italic text-center py-4">
                      Aucun observateur
                    </div>
                  )}
                </div>
              </div>

              {/* COLONNE 2 - ANALYSE (50%) */}
              <div className="flex-1 bg-red-50 rounded-lg border-2 border-red-200 p-3 flex flex-col">
                <div className="flex items-center justify-between gap-2 mb-3 border-b border-red-300 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-600 text-white rounded-full text-xs flex items-center justify-center font-bold">
                      2
                    </div>
                    <h3 className="text-xs font-bold text-red-800">ANALYSE</h3>
                  </div>
                  <button
                    onClick={() => setActiveMainAdder("analyse")}
                    className="flex items-center justify-center w-6 h-6 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                    title="Ajouter GroupeFlux"
                  >
                    <Plus size={16} />
                  </button>
                </div>

                {activeMainAdder === "analyse" && (
                  <div className="mb-3">
                    <SearchableSelect
                      options={availableGroups.groupeFlux.filter(
                        (gf) => !flux.analyse.some((a) => a.groupeFlux === gf)
                      )}
                      value=""
                      onChange={(val) => {
                        handleAddGroupeFlux(val);
                        setActiveMainAdder(null);
                      }}
                      placeholder="Sélectionner ou créer GroupeFlux..."
                      className="w-full"
                      allowCreate={true}
                      onCreate={handleCreateGroupeFlux}
                      startOpen={true}
                      onClose={() => setActiveMainAdder(null)}
                    />
                  </div>
                )}

                <div className="space-y-3 flex-1 overflow-y-auto">
                  {flux.analyse && flux.analyse.length > 0 ? (
                    flux.analyse.map((group, gIdx) => (
                      <div
                        key={gIdx}
                        className="bg-red-100 border border-red-300 rounded-lg p-3 space-y-2"
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2 flex-1 truncate">
                            <Briefcase
                              size={14}
                              className="text-red-500 flex-shrink-0"
                            />
                            <h4 className="text-sm font-semibold text-red-800 uppercase tracking-wide truncate">
                              {group.groupeFlux}
                            </h4>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Actions d'ajout */}
                            <button
                              onClick={() => handleToggleAdder(gIdx, "dalux")}
                              className="flex items-center justify-center w-7 h-7 bg-white text-gray-600 rounded-md hover:bg-gray-100 border border-gray-300 transition-colors"
                              title="Ajouter GroupeDalux"
                            >
                              <Users size={14} />
                            </button>
                            <button
                              onClick={() => handleToggleAdder(gIdx, "person")}
                              className="flex items-center justify-center w-7 h-7 bg-white text-gray-600 rounded-md hover:bg-gray-100 border border-gray-300 transition-colors"
                              title="Ajouter Personne"
                            >
                              <UserPlus size={14} />
                            </button>

                            <div className="h-5 w-px bg-gray-300"></div>

                            {/* Actions de déplacement */}
                            <button
                              onClick={() => handleMoveGroupeFlux(gIdx, "up")}
                              disabled={gIdx === 0}
                              className="flex items-center justify-center w-7 h-7 text-gray-600 rounded-full hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
                              title="Monter"
                            >
                              <ChevronUp size={16} />
                            </button>
                            <button
                              onClick={() => handleMoveGroupeFlux(gIdx, "down")}
                              disabled={gIdx === flux.analyse.length - 1}
                              className="flex items-center justify-center w-7 h-7 text-gray-600 rounded-full hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
                              title="Descendre"
                            >
                              <ChevronDown size={16} />
                            </button>

                            <div className="h-5 w-px bg-gray-300"></div>

                            {/* Action de suppression */}
                            <button
                              onClick={() => removeGroupeFlux(gIdx)}
                              className="flex items-center justify-center w-7 h-7 bg-white text-red-600 rounded-md hover:bg-red-50 border border-gray-300 hover:border-red-300 transition-colors"
                              title="Supprimer le groupe"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {activeAdder && activeAdder.gIdx === gIdx && (
                          <div className="my-2 transition-all duration-300">
                            {activeAdder.type === "dalux" && (
                              <SearchableSelect
                                options={availableGroups.groupeDalux.filter(
                                  (dalux) => !group.groupeDalux.includes(dalux)
                                )}
                                value=""
                                onChange={(selectedValue) => {
                                  if (selectedValue) {
                                    addGroupeDalux(gIdx, selectedValue);
                                  }
                                  setActiveAdder(null);
                                }}
                                placeholder="Rechercher un GroupeDalux..."
                                className="w-full"
                                allowCreate={true}
                                onCreate={handleCreateGroupeDalux}
                                startOpen={true}
                                onClose={() => setActiveAdder(null)}
                              />
                            )}
                            {activeAdder.type === "person" && (
                              <SearchableSelect
                                options={
                                  contactsData?.objects
                                    ? contactsData.objects
                                        .map(
                                          (contact) =>
                                            contact["Nom"] ||
                                            contact["nom"] ||
                                            ""
                                        )
                                        .filter(
                                          (nom) => nom && nom.trim() !== ""
                                        )
                                        .filter(
                                          (nom) =>
                                            !group.individuelPersons?.includes(
                                              nom
                                            )
                                        )
                                        .sort()
                                    : []
                                }
                                value=""
                                onChange={(selectedPerson) => {
                                  if (selectedPerson && selectedPerson.trim()) {
                                    addIndividualPerson(
                                      gIdx,
                                      selectedPerson.trim()
                                    );
                                  }
                                  setActiveAdder(null);
                                }}
                                placeholder="Rechercher une personne..."
                                className="w-full"
                                startOpen={true}
                                onClose={() => setActiveAdder(null)}
                              />
                            )}
                          </div>
                        )}

                        <div className="space-y-2 pt-1">
                          {/* GroupeDalux */}
                          {group.groupeDalux &&
                            group.groupeDalux.length > 0 && (
                              <div>
                                <div className="text-xs font-medium text-red-800 mb-1">
                                  Groupes Dalux
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {group.groupeDalux.map((dalux, dIdx) => (
                                    <div
                                      key={dIdx}
                                      className="bg-red-600 text-white px-2 py-1 rounded inline-flex items-center justify-between text-xs"
                                    >
                                      <div className="flex items-center gap-1.5 truncate">
                                        {!isFluxGroup(dalux) && (
                                          <WarningTooltip text="Ce groupe ne contient pas le mot 'flux'. Il pourrait ne pas être approprié pour un flux de validation.">
                                            <AlertTriangle
                                              size={12}
                                              className="text-yellow-300 flex-shrink-0"
                                            />
                                          </WarningTooltip>
                                        )}
                                        <span className="truncate">
                                          {dalux}
                                        </span>
                                      </div>
                                      <button
                                        onClick={() =>
                                          removeGroupeDalux(gIdx, dalux)
                                        }
                                        className="text-red-200 hover:text-white flex-shrink-0 ml-1"
                                      >
                                        <X size={12} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Personnes Individuelles */}
                          {group.individuelPersons &&
                            group.individuelPersons.length > 0 && (
                              <div>
                                <div className="text-xs font-medium text-gray-700 mb-1">
                                  Personnes individuelles
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {group.individuelPersons.map(
                                    (person, pIdx) => (
                                      <div
                                        key={pIdx}
                                        className="bg-gray-600 text-white px-2 py-1 rounded flex items-center gap-1 text-xs"
                                      >
                                        <span className="truncate">
                                          {person}
                                        </span>
                                        <button
                                          onClick={() =>
                                            removeIndividualPerson(gIdx, person)
                                          }
                                          className="text-gray-200 hover:text-white flex-shrink-0"
                                        >
                                          <X size={12} />
                                        </button>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 text-xs italic text-center py-4">
                      Aucune analyse
                    </div>
                  )}
                </div>
              </div>

              {/* COLONNE 3 - AUTORISER (25%) */}
              <div className="w-1/4 bg-green-50 rounded-lg border-2 border-green-200 p-3 flex flex-col">
                <div className="flex items-center justify-between gap-2 mb-3 border-b border-green-300 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-600 text-white rounded-full text-xs flex items-center justify-center font-bold">
                      3
                    </div>
                    <h3 className="text-xs font-bold text-green-800">
                      AUTORISER
                    </h3>
                  </div>
                  <button
                    onClick={() => setActiveMainAdder("autoriser")}
                    className="flex items-center justify-center w-6 h-6 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                    title="Ajouter GroupeDalux"
                  >
                    <Plus size={16} />
                  </button>
                </div>

                {activeMainAdder === "autoriser" && (
                  <div className="mb-3">
                    <SearchableSelect
                      options={availableGroups.groupeDalux}
                      value=""
                      onChange={(selectedValue) => {
                        if (
                          selectedValue &&
                          !flux.autoriser.includes(selectedValue)
                        ) {
                          if (!isFluxGroup(selectedValue)) {
                            addToast(
                              "Attention: Ce groupe n'est peut-être pas destiné à un flux.",
                              "info"
                            );
                          }
                          const updatedFlux = {
                            ...flux,
                            autoriser: [...flux.autoriser, selectedValue],
                          };
                          setEditingFlux(updatedFlux);
                        }
                        setActiveMainAdder(null);
                      }}
                      placeholder="+ Ajouter GroupeDalux"
                      className="w-full"
                      allowCreate={true}
                      onCreate={handleCreateGroupeDalux}
                      startOpen={true}
                      onClose={() => setActiveMainAdder(null)}
                    />
                  </div>
                )}

                <div className="flex flex-wrap content-start gap-1 flex-1 overflow-y-auto">
                  {flux.autoriser && flux.autoriser.length > 0 ? (
                    flux.autoriser.map((dalux, idx) => (
                      <div
                        key={idx}
                        className="bg-green-600 text-white px-2 py-1 rounded inline-flex items-center justify-between text-xs"
                      >
                        <div className="flex items-center gap-1.5 truncate">
                          {!isFluxGroup(dalux) && (
                            <WarningTooltip text="Ce groupe ne contient pas le mot 'flux'. Il pourrait ne pas être approprié pour un flux de validation.">
                              <AlertTriangle
                                size={12}
                                className="text-yellow-300 flex-shrink-0"
                              />
                            </WarningTooltip>
                          )}
                          <span className="truncate">{dalux}</span>
                        </div>
                        <button
                          onClick={() => removeAutoriser(dalux)}
                          className="text-green-200 hover:text-white ml-1 flex-shrink-0"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 text-xs italic text-center py-4">
                      Aucune autorisation
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 flex-shrink-0">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors font-medium text-xs"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            className="px-6 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm text-xs"
          >
            Sauvegarder
          </button>
        </div>
      </div>
    </div>
  );
};

const NewFluxModal = ({ onSave, onCancel }) => {
  const [fluxName, setFluxName] = useState("");

  const handleSave = () => {
    if (fluxName.trim()) {
      onSave(fluxName.trim());
      setFluxName("");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Nouveau Flux</h2>
        </div>

        <div className="p-6">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Nom du flux
          </label>
          <input
            type="text"
            value={fluxName}
            onChange={(e) => setFluxName(e.target.value)}
            placeholder="Entrez le nom du flux"
            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
            onKeyPress={(e) => e.key === "Enter" && handleSave()}
            autoFocus
          />
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!fluxName.trim()}
            className="px-4 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            Créer
          </button>
        </div>
      </div>
    </div>
  );
};

const CopyFluxModal = ({ fluxToCopy, onSave, onCancel }) => {
  const [fluxName, setFluxName] = useState("");

  useEffect(() => {
    if (fluxToCopy) {
      setFluxName(fluxToCopy.nom);
    }
  }, [fluxToCopy]);

  const handleSave = (shouldEdit) => {
    if (fluxName.trim()) {
      onSave(fluxName.trim(), shouldEdit);
    }
  };

  if (!fluxToCopy) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            Copier le Flux "{fluxToCopy.nom}"
          </h2>
        </div>
        <div className="p-6">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Nouveau nom du flux
          </label>
          <input
            type="text"
            value={fluxName}
            onChange={(e) => setFluxName(e.target.value)}
            placeholder="Entrez le nouveau nom du flux"
            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
            onKeyPress={(e) => e.key === "Enter" && handleSave(true)}
            autoFocus
          />
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={!fluxName.trim()}
            className="px-4 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            Copier
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={!fluxName.trim()}
            className="px-4 py-1.5 bg-green-600 text-white rounded-md text-xs font-medium hover:bg-green-700 disabled:bg-gray-400 transition-colors"
          >
            Copier et Éditer
          </button>
        </div>
      </div>
    </div>
  );
};

const ImportOptionsModal = ({ onReplace, onMerge, onCancel, stats }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800">
          Options d'importation des flux
        </h2>
      </div>

      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-600">
          Votre fichier a été analysé. Comment souhaitez-vous importer les
          données ?
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={onReplace}
            className="p-4 border border-red-200 bg-red-50 rounded-lg text-left hover:border-red-400 hover:bg-red-100 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <div className="flex items-start gap-3">
              <Replace size={24} className="text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-red-800">
                  Remplacer la liste actuelle
                </h3>
                <p className="text-xs text-red-700 mt-1">
                  La liste actuelle sera effacée et remplacée par le contenu du
                  fichier.
                </p>
                <div className="mt-2 text-xs font-bold text-red-900">
                  {stats.totalInFile} flux seront importés.
                </div>
              </div>
            </div>
          </button>

          <button
            onClick={onMerge}
            className="p-4 border border-blue-200 bg-blue-50 rounded-lg text-left hover:border-blue-400 hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <div className="flex items-start gap-3">
              <GitMerge
                size={24}
                className="text-blue-600 flex-shrink-0 mt-1"
              />
              <div>
                <h3 className="font-semibold text-blue-800">
                  Fusionner avec la liste actuelle
                </h3>
                <p className="text-xs text-blue-700 mt-1">
                  Les nouveaux flux seront ajoutés. Pour les flux existants,
                  vous pourrez choisir une action.
                </p>
                <div className="mt-2 text-xs">
                  <span className="font-bold text-green-600">
                    {stats.newFlux}
                  </span>{" "}
                  nouveaux flux à ajouter.
                  <br />
                  <span className="font-bold text-orange-600">
                    {stats.conflicts}
                  </span>{" "}
                  conflits à résoudre.
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Annuler l'importation
        </button>
      </div>
    </div>
  </div>
);

const ConflictResolutionModal = ({ conflicts, onResolve, onCancel }) => {
  const [resolutions, setResolutions] = useState({});

  useEffect(() => {
    // Default resolution to 'keep' for all conflicts
    const initialResolutions = {};
    conflicts.forEach((conflict) => {
      initialResolutions[conflict.nom] = "keep";
    });
    setResolutions(initialResolutions);
  }, [conflicts]);

  const handleResolutionChange = (fluxName, resolution) => {
    setResolutions((prev) => ({ ...prev, [fluxName]: resolution }));
  };

  const handleApplyToAll = (resolution) => {
    const allResolved = {};
    conflicts.forEach((conflict) => {
      allResolved[conflict.nom] = resolution;
    });
    setResolutions(allResolved);
  };

  const resolutionOptions = [
    {
      id: "replace",
      label: "Remplacer l'existant",
      icon: <Replace className="text-orange-500" />,
    },
    {
      id: "keep",
      label: "Garder l'existant",
      icon: <Archive className="text-blue-500" />,
    },
    {
      id: "keep_both",
      label: "Garder les deux (renommer)",
      icon: <CopyPlus className="text-green-500" />,
    },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">
            Résolution des conflits d'importation
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {conflicts.length} flux avec le même nom ont été trouvés.
          </p>
        </div>

        <div className="p-2 flex-shrink-0 border-b">
          <div className="flex items-center justify-between text-xs p-2 rounded bg-gray-50">
            <span className="font-medium text-gray-600">
              Appliquer à tous :
            </span>
            <div className="flex gap-2">
              {resolutionOptions.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => handleApplyToAll(opt.id)}
                  className="px-2 py-1 bg-white border rounded hover:bg-gray-100 flex items-center gap-1.5"
                >
                  {React.cloneElement(opt.icon, { size: 14 })}{" "}
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-grow overflow-y-auto p-4">
          <div className="space-y-2">
            {conflicts.map((conflict) => (
              <div
                key={conflict.nom}
                className="flex items-center justify-between p-2 rounded-lg border transition-colors bg-white hover:bg-gray-50"
              >
                <span className="font-medium text-sm text-gray-800 truncate pr-4">
                  {conflict.nom}
                </span>
                <div className="flex items-center gap-1 bg-gray-100 p-0.5 rounded-lg">
                  {resolutionOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() =>
                        handleResolutionChange(conflict.nom, option.id)
                      }
                      title={option.label}
                      className={`p-2 rounded-md transition-colors ${
                        resolutions[conflict.nom] === option.id
                          ? "bg-white shadow-sm"
                          : "text-gray-500 hover:bg-gray-200"
                      }`}
                    >
                      {React.cloneElement(option.icon, { size: 16 })}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 flex-shrink-0">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => onResolve(resolutions)}
            className="px-6 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors"
          >
            Confirmer et Fusionner
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Toast Notification System ---
const Toast = ({ message, type, onClose }) => {
  const icons = {
    success: <CheckCircle className="text-green-500" size={20} />,
    error: <AlertTriangle className="text-red-500" size={20} />,
    info: <Info className="text-blue-500" size={20} />,
  };

  const colors = {
    success: "bg-green-50 border-green-200 text-green-800",
    error: "bg-red-50 border-red-200 text-red-800",
    info: "bg-blue-50 border-blue-200 text-blue-800",
  };

  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(onClose, 4000);
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const handleClose = () => {
    setExiting(true);
    setTimeout(onClose, 300);
  };

  return (
    <div
      className={`relative flex items-center gap-3 w-full max-w-sm p-4 rounded-lg shadow-lg border transition-all duration-300 transform ${
        colors[type]
      } ${
        exiting ? "opacity-0 translate-x-full" : "opacity-100 translate-x-0"
      }`}
    >
      <div>{icons[type]}</div>
      <p className="text-xs font-medium">{message}</p>
      <button
        onClick={handleClose}
        className="absolute top-1 right-1 text-gray-500 hover:text-gray-800"
      >
        <X size={14} />
      </button>
    </div>
  );
};

const ToastContainer = ({ toasts, removeToast }) => (
  <div className="fixed top-4 right-4 z-[100] space-y-2">
    {toasts.map((toast) => (
      <Toast key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
    ))}
  </div>
);

const useToasts = () => {
  const [toasts, setToasts] = useState([]);
  const toastId = useRef(0);

  const addToast = (message, type = "success") => {
    const id = toastId.current++;
    setToasts((prev) => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return { toasts, addToast, removeToast };
};
// --- End Toast System ---

// Helper function to find people in a Dalux group
const findPeopleForDaluxGroup = (daluxGroup, contacts) => {
  if (!contacts?.objects) return [];
  return contacts.objects
    .filter((contact) => {
      const groupes = contact["Groupes"] || contact["groupes"] || "";
      return groupes.includes(daluxGroup);
    })
    .map((contact) => (contact["Nom"] || contact["nom"] || "").trim())
    .filter(Boolean); // Filter out empty names
};

const isFluxGroup = (groupName = "") => {
  if (typeof groupName !== "string") return true; // Default to true if not a string to avoid false positives
  return groupName.toLowerCase().includes("flux");
};

const normalizeInitiateur = (initiateur) => {
  if (!initiateur) {
    return { standard: [], observateur: [] };
  }
  if (
    typeof initiateur === "object" &&
    !Array.isArray(initiateur) &&
    initiateur.hasOwnProperty("standard")
  ) {
    return {
      standard: initiateur.standard || [],
      observateur: initiateur.observateur || [],
    };
  }
  if (Array.isArray(initiateur)) {
    return { standard: initiateur, observateur: [] };
  }
  return { standard: [], observateur: [] };
};

const parseDateString = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") {
    return new Date().toISOString();
  }

  // Check if it's already a valid ISO string
  const directParse = new Date(dateStr);
  if (!isNaN(directParse.getTime()) && dateStr.includes("-")) {
    return directParse.toISOString();
  }

  // Try to parse DD/MM/YYYY
  const parts = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (parts) {
    // parts[1] = DD, parts[2] = MM, parts[3] = YYYY
    const isoDate = new Date(Date.UTC(parts[3], parts[2] - 1, parts[1]));
    if (!isNaN(isoDate.getTime())) {
      return isoDate.toISOString();
    }
  }

  // Fallback for other invalid formats
  return new Date().toISOString();
};

const SortableHeader = ({ children, name, sortConfig, onSort }) => {
  const isSorted = sortConfig && sortConfig.key === name;
  const direction = isSorted ? sortConfig.direction : null;

  return (
    <th
      className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={() => onSort(name)}
    >
      <div className="flex items-center gap-1">
        <span>{children}</span>
        {direction === "ascending" && (
          <ChevronUp size={14} className="text-gray-600" />
        )}
        {direction === "descending" && (
          <ChevronDown size={14} className="text-gray-600" />
        )}
        {!direction && <ChevronsUpDown size={14} className="text-gray-400" />}
      </div>
    </th>
  );
};

const getStatusStyles = (status) => {
  switch (status) {
    case "valider":
      return "bg-green-100 text-green-800 border-green-200";
    case "en cours":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "en attente":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "a valider":
      return "bg-orange-100 text-orange-800 border-orange-200";
    case "a supprimer":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const StatusDropdown = ({ currentStatus, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const statuses = [
    { value: "en attente", label: "En attente" },
    { value: "en cours", label: "En cours" },
    { value: "a valider", label: "À valider" },
    { value: "valider", label: "Validé" },
    { value: "a supprimer", label: "À supprimer" },
  ];

  const selectedLabel = statuses.find(
    (s) => s.value === (currentStatus || "en attente")
  )?.label;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (statusValue) => {
    onStatusChange(statusValue);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full inline-flex items-center justify-between capitalize px-2.5 py-0.5 rounded-full text-xs font-medium border cursor-pointer transition-colors ${getStatusStyles(
          currentStatus || "en attente"
        )} hover:brightness-95`}
      >
        <span>{selectedLabel}</span>
        <ChevronDown size={14} className="ml-1 opacity-50" />
      </button>
      {isOpen && (
        <ul className="absolute z-20 mt-1 w-40 bg-white shadow-lg max-h-60 rounded-md py-1 text-xs ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
          {statuses.map((status) => (
            <li
              key={status.value}
              onClick={() => handleSelect(status.value)}
              className="text-gray-900 cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
            >
              <span
                className={`capitalize font-medium ${
                  currentStatus === status.value
                    ? "text-blue-600"
                    : "text-gray-900"
                }`}
              >
                {status.label}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FluxAdminInterface = () => {
  const [fluxData, setFluxData] = useState([]);
  const [editingFlux, setEditingFlux] = useState(null);
  const [copyingFlux, setCopyingFlux] = useState(null);
  const [showNewFluxModal, setShowNewFluxModal] = useState(false);
  const [availableGroups, setAvailableGroups] = useState({
    groupeFlux: [],
    groupeDalux: [],
  });
  const [contactsData, setContactsData] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [currentView, setCurrentView] = useState("flux");
  const { toasts, addToast, removeToast } = useToasts();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterInput, setFilterInput] = useState("");
  const [filterTags, setFilterTags] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "nom",
    direction: "ascending",
  });
  const [viewMode, setViewMode] = useState("all"); // 'all' or 'groupeFluxOnly'
  const [showTags, setShowTags] = useState(true);
  const [copiedConfig, setCopiedConfig] = useState(null);
  const [editingComment, setEditingComment] = useState(null); // { fluxId, initialComment, position: {x, y} }

  // State for import modals
  const [showImportOptions, setShowImportOptions] = useState(false);
  const [showConflictResolution, setShowConflictResolution] = useState(false);
  const [importStats, setImportStats] = useState({
    newFlux: 0,
    conflicts: 0,
    totalInFile: 0,
  });
  const [importedFluxData, setImportedFluxData] = useState([]);
  const [conflictingFlux, setConflictingFlux] = useState([]);

  // Load state from localStorage on initial render
  useEffect(() => {
    try {
      const savedFluxData = localStorage.getItem("fluxAdminData_flux");
      if (savedFluxData) {
        setFluxData(JSON.parse(savedFluxData));
      }

      const savedContactsData = localStorage.getItem("fluxAdminData_contacts");
      if (savedContactsData) {
        setContactsData(JSON.parse(savedContactsData));
      }

      const savedAvailableGroups = localStorage.getItem("fluxAdminData_groups");
      if (savedAvailableGroups) {
        setAvailableGroups(JSON.parse(savedAvailableGroups));
      }
    } catch (error) {
      console.error("Failed to load data from localStorage", error);
      addToast("Erreur au chargement des données locales.", "error");
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem("fluxAdminData_flux", JSON.stringify(fluxData));
    } catch (error) {
      console.error("Failed to save flux data to localStorage", error);
    }
  }, [fluxData]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "fluxAdminData_contacts",
        JSON.stringify(contactsData)
      );
    } catch (error) {
      console.error("Failed to save contacts data to localStorage", error);
    }
  }, [contactsData]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "fluxAdminData_groups",
        JSON.stringify(availableGroups)
      );
    } catch (error) {
      console.error("Failed to save groups data to localStorage", error);
    }
  }, [availableGroups]);

  const handleResetApplication = () => {
    // Clear localStorage
    localStorage.removeItem("fluxAdminData_flux");
    localStorage.removeItem("fluxAdminData_contacts");
    localStorage.removeItem("fluxAdminData_groups");

    // Reset component state
    setFluxData([]);
    setContactsData(null);
    setAvailableGroups({ groupeFlux: [], groupeDalux: [] });
    setSearchQuery("");
    setFilterTags([]);
    setCurrentView("flux");
    setEditingFlux(null);

    addToast("Application réinitialisée.", "info");
  };

  const exportData = useMemo(() => {
    const data = [];
    if (!fluxData || fluxData.length === 0) return data;

    fluxData.forEach((flux) => {
      const createRow = (
        etape,
        role,
        groupeFlux,
        groupeDalux,
        personne,
        initiatorRole
      ) => ({
        "Nom du flux": flux.nom,
        "Date de création": flux.createdAt
          ? new Date(flux.createdAt).toLocaleDateString("fr-FR")
          : "",
        Statut: flux.status || "en attente",
        Commentaire: flux.comment || "",
        "N° Etape": etape,
        Role: role,
        "Role Initiateur": initiatorRole || "",
        GroupeFlux: groupeFlux,
        GroupeDalux: groupeDalux,
        Personne: personne,
        Entreprise: "",
        "Type de partage": flux.typePartage || "Publié",
        Type: flux.type || "Mandataire",
      });

      // Etape 1: Initiateur
      if (flux.initiateur?.standard && flux.initiateur.standard.length > 0) {
        flux.initiateur.standard.forEach((dalux) => {
          const personnes = findPeopleForDaluxGroup(dalux, contactsData);
          if (personnes.length > 0) {
            personnes.forEach((p) =>
              data.push(createRow(1, "Initiateur", "", dalux, p, "Standard"))
            );
          } else {
            data.push(createRow(1, "Initiateur", "", dalux, "", "Standard"));
          }
        });
      }
      if (
        flux.initiateur?.observateur &&
        flux.initiateur.observateur.length > 0
      ) {
        flux.initiateur.observateur.forEach((dalux) => {
          const personnes = findPeopleForDaluxGroup(dalux, contactsData);
          if (personnes.length > 0) {
            personnes.forEach((p) =>
              data.push(createRow(1, "Initiateur", "", dalux, p, "Observateur"))
            );
          } else {
            data.push(createRow(1, "Initiateur", "", dalux, "", "Observateur"));
          }
        });
      }

      // Etape 2: Analyse
      if (flux.analyse && flux.analyse.length > 0) {
        flux.analyse.forEach((group) => {
          if (group.groupeDalux && group.groupeDalux.length > 0) {
            group.groupeDalux.forEach((dalux) => {
              const personnes = findPeopleForDaluxGroup(dalux, contactsData);
              if (personnes.length > 0) {
                personnes.forEach((p) =>
                  data.push(createRow(2, "Analyse", group.groupeFlux, dalux, p))
                );
              } else {
                data.push(createRow(2, "Analyse", group.groupeFlux, dalux, ""));
              }
            });
          }
          if (group.individuelPersons && group.individuelPersons.length > 0) {
            group.individuelPersons.forEach((p) => {
              data.push(
                createRow(2, "Analyse", group.groupeFlux, "Individuel", p)
              );
            });
          }
        });
      }

      // Etape 3: Autoriser
      if (flux.autoriser && flux.autoriser.length > 0) {
        flux.autoriser.forEach((dalux) => {
          const personnes = findPeopleForDaluxGroup(dalux, contactsData);
          if (personnes.length > 0) {
            personnes.forEach((p) =>
              data.push(createRow(3, "Autoriser", "", dalux, p))
            );
          } else {
            data.push(createRow(3, "Autoriser", "", dalux, ""));
          }
        });
      }
    });

    return data;
  }, [fluxData, contactsData]);

  const filteredAndSortedFlux = useMemo(() => {
    let sortableFlux = [...fluxData];

    // Filtering by main search query
    if (searchQuery.trim() !== "") {
      sortableFlux = sortableFlux.filter((flux) =>
        flux.nom.toLowerCase().includes(searchQuery.trim().toLowerCase())
      );
    }

    // Filtering by tags
    if (filterTags.length > 0) {
      sortableFlux = sortableFlux.filter((flux) => {
        return filterTags.every((tag) =>
          flux.nom.toLowerCase().includes(tag.toLowerCase())
        );
      });
    }

    // Sorting
    if (sortConfig !== null) {
      sortableFlux.sort((a, b) => {
        const valA = a[sortConfig.key] || "";
        const valB = b[sortConfig.key] || "";
        if (valA < valB) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (valA > valB) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableFlux;
  }, [fluxData, searchQuery, filterTags, sortConfig]);

  const handleImportFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls,.csv";
    input.onchange = handleFileSelected;
    input.click();
  };

  const handleImportContacts = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls";
    input.onchange = handleContactsSelected;
    input.click();
  };

  const handleContactsSelected = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);

    try {
      const data = await importContactsFile(file);

      if (data && data.length > 0) {
        const nouveauxGroupesDalux = extractGroupesDaluxFromContacts(data);
        const groupesDaluxUniques = [
          ...new Set([...availableGroups.groupeDalux, ...nouveauxGroupesDalux]),
        ].sort();

        const headers = data[0];
        const contactsObjects = [];

        for (let i = 1; i < data.length; i++) {
          const row = data[i];
          const contactObj = {};
          headers.forEach((header, index) => {
            contactObj[header] = row[index] || "";
          });
          contactsObjects.push(contactObj);
        }

        setContactsData({
          raw: data,
          objects: contactsObjects,
          headers: headers,
        });

        setAvailableGroups((prev) => ({
          ...prev,
          groupeDalux: groupesDaluxUniques,
        }));

        addToast(
          `Import Contacts réussi! ${data.length - 1} contacts importés.`,
          "success"
        );
      }
    } catch (error) {
      addToast(`Erreur lors de l'import contacts: ${error.message}`, "error");
    } finally {
      setIsImporting(false);
    }
  };

  const importContactsFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          if (!window.XLSX) {
            const script = document.createElement("script");
            script.src =
              "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
            script.onload = () => processContactsData();
            script.onerror = () =>
              reject(new Error("Impossible de charger la librairie Excel"));
            document.head.appendChild(script);
          } else {
            processContactsData();
          }

          function processContactsData() {
            try {
              const data = new Uint8Array(e.target.result);
              const workbook = window.XLSX.read(data, { type: "array" });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];

              const jsonData = window.XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
                defval: "",
                blankrows: false,
              });

              if (jsonData.length === 0) {
                reject(new Error("Le fichier Excel semble vide"));
                return;
              }

              resolve(jsonData);
            } catch (error) {
              reject(new Error(`Erreur de traitement Excel: ${error.message}`));
            }
          }
        } catch (error) {
          reject(new Error(`Erreur de lecture Excel: ${error.message}`));
        }
      };

      reader.onerror = () => reject(new Error("Erreur de lecture du fichier"));
      reader.readAsArrayBuffer(file);
    });
  };

  const extractGroupesDaluxFromContacts = (contactsData) => {
    const groupesDaluxSet = new Set();
    if (contactsData.length === 0) return [];

    const headers = contactsData[0] || [];
    let groupesColumnIndex = -1;

    headers.forEach((header, index) => {
      // Find the first column that seems to be about groups.
      if (
        groupesColumnIndex === -1 &&
        header &&
        typeof header === "string" &&
        header.toLowerCase().trim().includes("groupe")
      ) {
        groupesColumnIndex = index;
      }
    });

    if (groupesColumnIndex === -1) {
      console.warn(
        "Aucune colonne de groupes ('Groupes') n'a été trouvée dans le fichier de contacts."
      );
      return [];
    }

    for (let i = 1; i < contactsData.length; i++) {
      const row = contactsData[i];
      if (!row || !Array.isArray(row)) continue;

      const groupes = row[groupesColumnIndex];

      if (groupes && typeof groupes === "string" && groupes.trim() !== "") {
        // Split by semicolon or comma for more flexibility
        const groupesList = groupes
          .split(/[;,]/)
          .map((g) => g.trim())
          .filter((g) => g.length > 0);

        groupesList.forEach((groupe) => {
          groupesDaluxSet.add(groupe);
        });
      }
    }

    return Array.from(groupesDaluxSet).sort();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsImporting(true);
    try {
      let data;
      if (file.name.endsWith(".csv")) {
        data = await importCSVFile(file);
      } else {
        data = await importExcelFile(file);
      }

      if (data && data.length > 0) {
        const processedFlux = processFluxData(data);
        const existingFluxNames = new Set(fluxData.map((f) => f.nom));
        const newFlux = [];
        const conflicts = [];

        processedFlux.forEach((flux) => {
          if (existingFluxNames.has(flux.nom)) {
            conflicts.push(flux);
          } else {
            newFlux.push(flux);
          }
        });

        setImportedFluxData(processedFlux);
        setConflictingFlux(conflicts);
        setImportStats({
          newFlux: newFlux.length,
          conflicts: conflicts.length,
          totalInFile: processedFlux.length,
        });

        if (fluxData.length === 0) {
          handleReplaceImport(processedFlux);
        } else {
          setShowImportOptions(true);
        }
      }
    } catch (error) {
      addToast(
        `Erreur lors de la lecture du fichier: ${error.message}`,
        "error"
      );
    } finally {
      setIsImporting(false);
    }
  };

  // --- Import Logic Handlers ---
  const handleReplaceImport = (dataToImport) => {
    const data = dataToImport || importedFluxData;
    setFluxData(data);
    extractAvailableGroups(data.flatMap((f) => fluxToRawData(f)));
    setShowImportOptions(false);
    addToast(
      `Importation réussie. ${data.length} flux ont été chargés.`,
      "success"
    );
  };

  const handleMergeImport = () => {
    setShowImportOptions(false);
    if (conflictingFlux.length > 0) {
      setShowConflictResolution(true);
    } else {
      // No conflicts, just add new ones
      const newFluxes = importedFluxData.filter(
        (impFlux) => !fluxData.some((exFlux) => exFlux.nom === impFlux.nom)
      );
      const updatedFluxData = [...fluxData, ...newFluxes];
      setFluxData(updatedFluxData);
      extractAvailableGroups(updatedFluxData.flatMap((f) => fluxToRawData(f)));
      addToast(`${newFluxes.length} nouveaux flux ont été ajoutés.`, "success");
    }
  };

  const handleConflictResolution = (resolutions) => {
    let currentFluxData = [...fluxData];
    const newFluxesToAdd = [];

    importedFluxData.forEach((importedFlux) => {
      const resolution = resolutions[importedFlux.nom];
      if (resolution) {
        // It's a conflict
        if (resolution === "replace") {
          currentFluxData = currentFluxData.map((f) =>
            f.nom === importedFlux.nom ? importedFlux : f
          );
        } else if (resolution === "keep_both") {
          let newName = `${importedFlux.nom} (1)`;
          let i = 1;
          while (
            currentFluxData.some((f) => f.nom === newName) ||
            newFluxesToAdd.some((f) => f.nom === newName)
          ) {
            i++;
            newName = `${importedFlux.nom} (${i})`;
          }
          newFluxesToAdd.push({ ...importedFlux, nom: newName, id: newName });
        }
        // 'keep' does nothing
      } else {
        // It's a new flux
        newFluxesToAdd.push(importedFlux);
      }
    });

    const finalFluxData = [...currentFluxData, ...newFluxesToAdd];
    setFluxData(finalFluxData);
    extractAvailableGroups(finalFluxData.flatMap((f) => fluxToRawData(f)));
    setShowConflictResolution(false);
    addToast("Fusion terminée avec succès !", "success");
  };

  // Helper to convert flux object back to a structure processable by extractAvailableGroups
  const fluxToRawData = (flux) => {
    const raw = [];
    const allInitiateurs = [
      ...(flux.initiateur?.standard || []),
      ...(flux.initiateur?.observateur || []),
    ];
    allInitiateurs.forEach((g) => raw.push({ GroupeDalux: g }));
    flux.analyse.forEach((a) => {
      raw.push({ GroupeFlux: a.groupeFlux });
      a.groupeDalux.forEach((g) => raw.push({ GroupeDalux: g }));
    });
    flux.autoriser.forEach((g) => raw.push({ GroupeDalux: g }));
    return raw;
  };

  const importExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          if (!window.XLSX) {
            const script = document.createElement("script");
            script.src =
              "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
            script.onload = () => processExcelData();
            script.onerror = () =>
              reject(new Error("Impossible de charger la librairie Excel"));
            document.head.appendChild(script);
          } else {
            processExcelData();
          }

          function processExcelData() {
            try {
              const data = new Uint8Array(e.target.result);
              const workbook = window.XLSX.read(data, { type: "array" });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];

              const jsonData = window.XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
                defval: "",
                blankrows: false,
              });

              if (jsonData.length === 0) {
                reject(new Error("Le fichier Excel semble vide"));
                return;
              }

              resolve(jsonData);
            } catch (error) {
              reject(new Error(`Erreur de traitement Excel: ${error.message}`));
            }
          }
        } catch (error) {
          reject(new Error(`Erreur de lecture Excel: ${error.message}`));
        }
      };

      reader.onerror = () => reject(new Error("Erreur de lecture du fichier"));
      reader.readAsArrayBuffer(file);
    });
  };

  const importCSVFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          let csv = e.target.result;
          // Remove BOM if present
          if (csv.charCodeAt(0) === 0xfeff) {
            csv = csv.slice(1);
          }

          const rows = [[]];
          // This regex handles quoted fields, escaped quotes, and various line endings.
          const regex =
            /(;|\r?\n|\r|^)(?:"([^"]*(?:""[^"]*)*)"|([^";\r\n]*))/gi;
          let match;

          while ((match = regex.exec(csv))) {
            const delimiter = match[1];
            // If the delimiter is a newline, start a new row.
            if (delimiter.length && delimiter !== ";") {
              rows.push([]);
            }

            let value;
            if (match[2]) {
              // Quoted field
              value = match[2].replace(/""/g, '"');
            } else {
              // Unquoted field
              value = match[3];
            }

            rows[rows.length - 1].push(value);
          }

          // Remove last row if it's empty, which can happen with a trailing newline
          if (
            rows.length > 0 &&
            (rows[rows.length - 1].length === 0 ||
              (rows[rows.length - 1].length === 1 &&
                rows[rows.length - 1][0] === ""))
          ) {
            rows.pop();
          }

          if (rows.length < 2) {
            // Need at least a header and one data row
            resolve([]);
            return;
          }

          // Map to objects
          const headers = rows[0].map((h) => (h ? h.trim() : ""));
          const data = rows.slice(1).map((rowArray) => {
            const rowObject = {};
            headers.forEach((header, index) => {
              if (header) {
                // Only add keys for valid headers
                rowObject[header] = rowArray[index] || "";
              }
            });
            return rowObject;
          });

          resolve(data);
        } catch (error) {
          reject(new Error(`Erreur de lecture CSV: ${error.message}`));
        }
      };
      reader.onerror = () => reject(new Error("Erreur de lecture du fichier"));
      reader.readAsText(file, "utf-8");
    });
  };

  const processFluxData = (rawData) => {
    const fluxMap = new Map();

    rawData.forEach((row) => {
      const fluxName =
        row["Nom du flux"] || row["Nom_du_flux"] || row["flux"] || row["Flux"];
      if (!fluxName || fluxName.toString().trim() === "") return;

      if (!fluxMap.has(fluxName)) {
        const type = row["Type"] || row["type"];
        const typePartage = row["Type de partage"] || row["Type_de_partage"];
        const dateValue = row["Date de création"] || row["createdAt"];
        const createdAt = dateValue
          ? parseDateString(dateValue)
          : new Date().toISOString();
        const status = row["Statut"] || row["statut"] || "en attente";
        const comment = row["Commentaire"] || row["commentaire"] || "";

        fluxMap.set(fluxName, {
          id: fluxName,
          nom: fluxName,
          initiateur: { standard: [], observateur: [] },
          analyse: [],
          autoriser: [],
          type: type || "Mandataire",
          typePartage: typePartage || "Publié",
          createdAt: createdAt,
          status: status,
          comment: comment,
        });
      }

      const flux = fluxMap.get(fluxName);
      const role = row["Role"] || row["role"] || "";
      const gFlux = (
        row["GroupeFlux"] ||
        row["Groupe_Flux"] ||
        row["groupeflux"] ||
        ""
      )
        .toString()
        .trim();
      const gDalux = (
        row["GroupeDalux"] ||
        row["Groupe_Dalux"] ||
        row["groupedalux"] ||
        ""
      )
        .toString()
        .trim();
      const personne = (row["Personne"] || row["personne"] || "")
        .toString()
        .trim();

      const roleLower = role.toString().toLowerCase();

      if (roleLower.includes("initiateur")) {
        const initiatorRole = (
          row["Role Initiateur"] ||
          row["role_initiateur"] ||
          ""
        )
          .toString()
          .toLowerCase();
        if (gDalux) {
          if (initiatorRole.includes("observateur")) {
            if (!flux.initiateur.observateur.includes(gDalux)) {
              flux.initiateur.observateur.push(gDalux);
            }
          } else {
            // Par défaut, ou si "Standard", ou si colonne absente
            if (!flux.initiateur.standard.includes(gDalux)) {
              flux.initiateur.standard.push(gDalux);
            }
          }
        }
      } else if (roleLower.includes("analyse")) {
        let existingGroup = flux.analyse.find((g) => g.groupeFlux === gFlux);
        if (!existingGroup) {
          existingGroup = {
            groupeFlux: gFlux,
            groupeDalux: [],
            individuelPersons: [],
          };
          flux.analyse.push(existingGroup);
        }

        if (gDalux.toLowerCase() === "individuel" && personne) {
          if (!existingGroup.individuelPersons.includes(personne)) {
            existingGroup.individuelPersons.push(personne);
          }
        } else if (gDalux && !existingGroup.groupeDalux.includes(gDalux)) {
          existingGroup.groupeDalux.push(gDalux);
        }
      } else if (roleLower.includes("autoriser")) {
        if (gDalux && !flux.autoriser.includes(gDalux)) {
          flux.autoriser.push(gDalux);
        }
      }
    });

    return Array.from(fluxMap.values());
  };

  const extractAvailableGroups = (rawData) => {
    const groupeFluxSet = new Set(availableGroups.groupeFlux);
    const groupeDaluxSet = new Set(availableGroups.groupeDalux);

    rawData.forEach((row) => {
      const gFlux =
        row["GroupeFlux"] || row["Groupe_Flux"] || row["groupeflux"] || "";
      const gDalux =
        row["GroupeDalux"] || row["Groupe_Dalux"] || row["groupedalux"] || "";

      if (gFlux && gFlux.toString().trim()) {
        groupeFluxSet.add(gFlux.toString().trim());
      }
      if (gDalux && gDalux.toString().trim()) {
        groupeDaluxSet.add(gDalux.toString().trim());
      }
    });

    setAvailableGroups({
      groupeFlux: Array.from(groupeFluxSet).sort(),
      groupeDalux: Array.from(groupeDaluxSet).sort(),
    });
  };

  const handleEditFlux = (flux) => {
    const fluxCopy = JSON.parse(JSON.stringify(flux));
    // Conserver l'ID original pour pouvoir retrouver le flux même si son nom (et donc son ID) change
    fluxCopy.initiateur = normalizeInitiateur(fluxCopy.initiateur);
    setEditingFlux({ ...fluxCopy, originalId: flux.id });
  };

  const handleSaveFlux = () => {
    const { originalId, ...fluxToSave } = editingFlux;

    if (!fluxToSave.nom || fluxToSave.nom.trim() === "") {
      addToast("Le nom du flux ne peut pas être vide.", "error");
      return;
    }

    const isNameTaken = fluxData.some(
      (f) => f.id !== originalId && f.nom === fluxToSave.nom
    );

    if (isNameTaken) {
      addToast(
        `Un autre flux avec le nom "${fluxToSave.nom}" existe déjà.`,
        "error"
      );
      return;
    }

    const updatedFluxData = fluxData.map((f) => {
      if (f.id === originalId) {
        // Mettre à jour le flux et son ID pour correspondre au nouveau nom
        return { ...fluxToSave, id: fluxToSave.nom };
      }
      return f;
    });

    setFluxData(updatedFluxData);
    updateGlobalAvailableGroups(updatedFluxData);
    setEditingFlux(null);
    addToast(`Flux "${fluxToSave.nom}" sauvegardé avec succès.`, "success");
  };

  const handleAddFlux = () => setShowNewFluxModal(true);

  const handleSaveNewFlux = (newFluxName) => {
    const existingFlux = fluxData.find(
      (f) => f.id === newFluxName || f.nom === newFluxName
    );
    if (existingFlux) {
      addToast(`Un flux avec ce nom existe déjà : "${newFluxName}"`, "error");
      return;
    }

    const newFlux = {
      id: newFluxName,
      nom: newFluxName,
      initiateur: { standard: [], observateur: [] },
      analyse: [],
      autoriser: [],
      createdAt: new Date().toISOString(),
      status: "en attente",
      comment: "",
    };

    const updatedFluxData = [...fluxData, newFlux];
    setFluxData(updatedFluxData);
    updateGlobalAvailableGroups(updatedFluxData);
    setShowNewFluxModal(false);

    const fluxToEdit = JSON.parse(JSON.stringify(newFlux));
    setEditingFlux({ ...fluxToEdit, originalId: fluxToEdit.id });
  };

  const handleCopyFlux = (fluxToCopy) => {
    setCopyingFlux(fluxToCopy);
  };

  const handleSaveCopiedFlux = (newFluxName, shouldEdit) => {
    const existingFlux = fluxData.find(
      (f) => f.id === newFluxName || f.nom === newFluxName
    );
    if (existingFlux) {
      addToast(`Un flux avec ce nom existe déjà : "${newFluxName}"`, "error");
      return;
    }

    const newFlux = JSON.parse(JSON.stringify(copyingFlux));

    newFlux.id = newFluxName;
    newFlux.nom = newFluxName;
    newFlux.createdAt = new Date().toISOString();

    const updatedFluxData = [...fluxData, newFlux];
    setFluxData(updatedFluxData);

    setAvailableGroups((prev) => {
      const newGroupeFluxSet = new Set(prev.groupeFlux);
      const newGroupeDaluxSet = new Set(prev.groupeDalux);

      newFlux.analyse.forEach(
        (a) => a.groupeFlux && newGroupeFluxSet.add(a.groupeFlux)
      );

      return {
        groupeFlux: Array.from(newGroupeFluxSet).sort(),
        groupeDalux: Array.from(newGroupeDaluxSet).sort(),
      };
    });

    const originalName = copyingFlux.nom;
    setCopyingFlux(null);

    addToast(`Flux "${originalName}" copié vers "${newFluxName}".`, "success");

    if (shouldEdit) {
      setEditingFlux({ ...newFlux, originalId: newFlux.id });
    }
  };

  const handleDeleteFlux = (fluxId) => {
    const fluxToDelete = fluxData.find((flux) => flux.id === fluxId);
    if (!fluxToDelete) return;

    const nouveauxFlux = fluxData.filter((flux) => flux.id !== fluxId);
    setFluxData(nouveauxFlux);

    // Prune GroupeFlux if they are no longer used anywhere
    const allUsedGroupesFluxAfterDelete = new Set();
    nouveauxFlux.forEach((f) => {
      if (f.analyse) {
        f.analyse.forEach(
          (a) => a.groupeFlux && allUsedGroupesFluxAfterDelete.add(a.groupeFlux)
        );
      }
    });

    const groupesFluxInDeletedFlux = new Set(
      fluxToDelete.analyse.map((a) => a.groupeFlux)
    );
    const groupesToPrune = [...groupesFluxInDeletedFlux].filter(
      (g) => !allUsedGroupesFluxAfterDelete.has(g)
    );

    if (groupesToPrune.length > 0) {
      setAvailableGroups((prev) => ({
        ...prev,
        groupeFlux: prev.groupeFlux.filter((g) => !groupesToPrune.includes(g)),
      }));
    }

    if (fluxToDelete) {
      addToast(`Flux "${fluxToDelete.nom}" supprimé.`, "info");
    }
  };

  const handleCopyConfig = (fluxToCopy) => {
    const config = {
      initiateur: JSON.parse(JSON.stringify(fluxToCopy.initiateur || [])),
      analyse: JSON.parse(JSON.stringify(fluxToCopy.analyse || [])),
      autoriser: JSON.parse(JSON.stringify(fluxToCopy.autoriser || [])),
    };
    setCopiedConfig(config);
    addToast(`Configuration du flux "${fluxToCopy.nom}" copiée.`, "success");
  };

  const handlePasteConfig = () => {
    if (!copiedConfig) {
      addToast("Aucune configuration n'a été copiée.", "info");
      return;
    }
    if (!editingFlux) return;

    const updatedFluxWithPastedConfig = {
      ...editingFlux,
      initiateur: JSON.parse(JSON.stringify(copiedConfig.initiateur)),
      analyse: JSON.parse(JSON.stringify(copiedConfig.analyse)),
      autoriser: JSON.parse(JSON.stringify(copiedConfig.autoriser)),
    };

    setEditingFlux(updatedFluxWithPastedConfig);
    addToast("Configuration collée avec succès.", "success");
  };

  const handleStatusChange = (fluxId, newStatus) => {
    const updatedFluxData = fluxData.map((flux) => {
      if (flux.id === fluxId) {
        return { ...flux, status: newStatus };
      }
      return flux;
    });
    setFluxData(updatedFluxData);
    const fluxName = fluxData.find((f) => f.id === fluxId)?.nom;
    addToast(`Statut du flux "${fluxName}" mis à jour.`, "info");
  };

  const handleOpenCommentEditor = (e, flux) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const editorWidth = 320; // Corresponds à w-80 dans Tailwind
    const editorHeight = 210; // Estimation de la hauteur de l'éditeur

    // Centre horizontalement par rapport à l'icône, puis s'assure qu'il reste dans la fenêtre
    let x = rect.left + rect.width / 2 - editorWidth / 2;
    if (x < 10) {
      x = 10;
    } else if (x + editorWidth > window.innerWidth) {
      x = window.innerWidth - editorWidth - 10;
    }

    // Se positionne sous l'icône, mais passe au-dessus si cela sort de l'écran
    let y = rect.bottom + 10;
    if (y + editorHeight > window.innerHeight) {
      y = rect.top - editorHeight - 10;
    }

    // S'assure qu'il n'est pas en dehors du haut de l'écran
    if (y < 10) {
      y = 10;
    }

    setEditingComment({
      fluxId: flux.id,
      initialComment: flux.comment || "",
      position: { x, y },
    });
  };

  const handleSaveComment = (fluxId, newComment) => {
    setFluxData(
      fluxData.map((f) => (f.id === fluxId ? { ...f, comment: newComment } : f))
    );
    setEditingComment(null);
    addToast("Commentaire sauvegardé.", "success");
  };

  const updateGlobalAvailableGroups = (allFluxData) => {
    const globalGroupeFluxSet = new Set(availableGroups.groupeFlux);
    const globalGroupeDaluxSet = new Set(availableGroups.groupeDalux);

    allFluxData.forEach((flux) => {
      if (flux.initiateur) {
        const allInitiateurs = [
          ...(flux.initiateur?.standard || []),
          ...(flux.initiateur?.observateur || []),
        ];
        allInitiateurs.forEach((dalux) => {
          if (dalux && dalux.trim()) {
            globalGroupeDaluxSet.add(dalux.trim());
          }
        });
      }

      if (flux.analyse) {
        flux.analyse.forEach((group) => {
          if (group.groupeFlux && group.groupeFlux.trim()) {
            globalGroupeFluxSet.add(group.groupeFlux.trim());
          }
          if (group.groupeDalux) {
            group.groupeDalux.forEach((dalux) => {
              if (dalux && dalux.trim()) {
                globalGroupeDaluxSet.add(dalux.trim());
              }
            });
          }
        });
      }

      if (flux.autoriser) {
        flux.autoriser.forEach((dalux) => {
          if (dalux && dalux.trim()) {
            globalGroupeDaluxSet.add(dalux.trim());
          }
        });
      }
    });

    setAvailableGroups({
      groupeFlux: Array.from(globalGroupeFluxSet).sort(),
      groupeDalux: Array.from(globalGroupeDaluxSet).sort(),
    });
  };

  const removeGroupeFlux = (index) => {
    const nouveauFlux = { ...editingFlux };
    nouveauFlux.analyse = editingFlux.analyse.filter((_, i) => i !== index);
    setEditingFlux(nouveauFlux);
  };

  const addGroupeDalux = (gFluxIndex, daluxName) => {
    if (!daluxName) return;

    if (!isFluxGroup(daluxName)) {
      addToast(
        "Attention: Ce groupe n'est peut-être pas destiné à un flux.",
        "info"
      );
    }

    const nouveauFlux = { ...editingFlux };
    nouveauFlux.analyse = [...editingFlux.analyse];
    const targetGroup = { ...editingFlux.analyse[gFluxIndex] };

    if (!targetGroup.groupeDalux.includes(daluxName)) {
      targetGroup.groupeDalux = [...targetGroup.groupeDalux, daluxName];
      nouveauFlux.analyse[gFluxIndex] = targetGroup;
      setEditingFlux(nouveauFlux);
    }
  };

  const removeGroupeDalux = (gFluxIndex, daluxName) => {
    const nouveauFlux = { ...editingFlux };
    nouveauFlux.analyse = [...editingFlux.analyse];
    const targetGroup = { ...editingFlux.analyse[gFluxIndex] };
    targetGroup.groupeDalux = targetGroup.groupeDalux.filter(
      (d) => d !== daluxName
    );
    nouveauFlux.analyse[gFluxIndex] = targetGroup;
    setEditingFlux(nouveauFlux);
  };

  const exportToExcel = async () => {
    try {
      console.log("Début export CSV...");
      const excelData = exportData;
      console.log("Données générées:", excelData.length, "lignes");

      if (excelData.length === 0) {
        addToast("Aucune donnée à exporter !", "info");
        return;
      }

      const lignesAvecPersonnes = excelData.filter(
        (row) => row.Personne && row.Personne.trim()
      ).length;

      const headers = [
        "Nom du flux",
        "Date de création",
        "Statut",
        "Commentaire",
        "N° Etape",
        "Role",
        "Role Initiateur",
        "GroupeFlux",
        "GroupeDalux",
        "Personne",
        "Entreprise",
        "Type de partage",
        "Type",
      ];
      let csvContent = headers.join(";") + "\n";

      excelData.forEach((row) => {
        const values = headers.map((header) => {
          const value = row[header] || "";
          return `"${value.replace(/"/g, '""')}"`;
        });
        csvContent += values.join(";") + "\n";
      });

      const BOM = "\uFEFF";
      const csvWithBOM = BOM + csvContent;

      const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:\-T]/g, "");
      const filename = `Base_donnee_extraction_${timestamp}.csv`;

      link.href = url;
      link.download = filename;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      console.log("Export réussi!");
      addToast(
        `Export terminé! ${excelData.length} lignes générées.`,
        "success"
      );
    } catch (error) {
      console.error("Erreur export:", error);
      addToast(`Erreur lors de l'export: ${error.message}`, "error");
    }
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleFilterKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && filterInput.trim() !== "") {
      e.preventDefault();
      const newTag = filterInput.trim();
      if (!filterTags.includes(newTag)) {
        setFilterTags([...filterTags, newTag]);
      }
      setFilterInput("");
    }
  };

  const removeFilterTag = (tagToRemove) => {
    setFilterTags(filterTags.filter((tag) => tag !== tagToRemove));
  };

  // Fonction d'export dédiée pour la Vue Export
  const handleExportFromView = () => {
    console.log("handleExportFromView appelée");

    try {
      const data = exportData;
      console.log("Données export générées:", data.length);

      if (data.length === 0) {
        addToast("Aucune donnée à exporter ! Vérifiez vos flux.", "info");
        return;
      }

      const headers = [
        "Nom du flux",
        "Date de création",
        "Statut",
        "Commentaire",
        "N° Etape",
        "Role",
        "Role Initiateur",
        "GroupeFlux",
        "GroupeDalux",
        "Personne",
        "Entreprise",
        "Type de partage",
        "Type",
      ];
      let csvContent = headers.join(";") + "\n";

      data.forEach((row) => {
        const values = headers.map((header) => {
          const value = row[header] || "";
          return `"${value.toString().replace(/"/g, '""')}"`;
        });
        csvContent += values.join(";") + "\n";
      });

      const BOM = "\uFEFF";
      const csvWithBOM = BOM + csvContent;

      const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const now = new Date();
      const timestamp =
        now.getFullYear() +
        String(now.getMonth() + 1).padStart(2, "0") +
        String(now.getDate()).padStart(2, "0") +
        "_" +
        String(now.getHours()).padStart(2, "0") +
        String(now.getMinutes()).padStart(2, "0") +
        String(now.getSeconds()).padStart(2, "0");

      const filename = `Export_Flux_${timestamp}.csv`;

      link.href = url;
      link.download = filename;
      link.style.display = "none";

      document.body.appendChild(link);
      console.log("Clic sur le lien de téléchargement...");
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      const lignesAvecPersonnes = data.filter(
        (row) => row.Personne && row.Personne.trim()
      ).length;
      addToast(`✅ Export réussi! ${data.length} lignes exportées.`, "success");
    } catch (error) {
      console.error("Erreur dans handleExportFromView:", error);
      addToast(`❌ Erreur lors de l'export: ${error.message}`, "error");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 sm:px-6">
          <nav className="flex space-x-1">
            <button
              onClick={() => setCurrentView("flux")}
              className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
                currentView === "flux"
                  ? "border-blue-500 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Layers size={16} className="inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Vue </span>Flux
            </button>

            <button
              onClick={() => setCurrentView("export")}
              className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
                currentView === "export"
                  ? "border-blue-500 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Users size={16} className="inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Vue </span>Export
            </button>
          </nav>
        </div>
      </div>

      <div className="p-2 sm:p-4 lg:p-6">
        <div className="w-full max-w-full">
          {currentView === "flux" ? (
            <div>
              <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6 mb-3 sm:mb-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
                  <div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                      Administration des Flux
                    </h1>
                    <p className="text-gray-600 mt-1 text-xs sm:text-sm">
                      Importez vos données de flux et contacts
                    </p>
                    <div className="text-xs text-blue-600 mt-1">
                      {fluxData.length} flux -{" "}
                      {availableGroups.groupeDalux.length} GroupeDalux -{" "}
                      {contactsData?.objects?.length || 0} contacts
                      {isImporting && (
                        <span className="ml-2 text-orange-600">
                          Import en cours...
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <button
                      onClick={handleImportFile}
                      disabled={isImporting}
                      className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed px-3 py-1.5 rounded-md flex items-center justify-center gap-2 transition-colors font-medium shadow-sm text-xs"
                    >
                      {isImporting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                          <span>Import...</span>
                        </>
                      ) : (
                        <>
                          <Plus size={14} />
                          <span>Importer Flux</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleImportContacts}
                      disabled={isImporting}
                      className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed px-3 py-1.5 rounded-md flex items-center justify-center gap-2 transition-colors font-medium shadow-sm text-xs"
                    >
                      {isImporting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                          <span>Import...</span>
                        </>
                      ) : (
                        <>
                          <Users size={14} />
                          <span>Importer Contacts</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleExportFromView}
                      className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-1.5 rounded-md flex items-center justify-center gap-2 transition-colors font-medium shadow-sm text-xs"
                      title="Exporter toutes les données en CSV"
                    >
                      <Save size={14} />
                      <span>Exporter CSV</span>
                    </button>

                    <button
                      onClick={handleResetApplication}
                      className="bg-white border border-gray-300 text-gray-700 hover:bg-red-50 hover:border-red-400 hover:text-red-700 px-3 py-1.5 rounded-md flex items-center justify-center gap-2 transition-colors font-medium shadow-sm text-xs"
                      title="Effacer toutes les données et réinitialiser l'application"
                    >
                      <Trash2 size={14} />
                      <span>Réinitialiser</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-6">
                <div className="bg-white p-2 sm:p-4 rounded-lg shadow-sm border">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Layers className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="ml-2 sm:ml-3">
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                        Flux
                      </p>
                      <p className="text-md sm:text-lg font-bold text-gray-900">
                        {fluxData.length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-2 sm:p-4 rounded-lg shadow-sm border">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Users className="h-4 w-4 text-orange-600" />
                    </div>
                    <div className="ml-2 sm:ml-3">
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                        GroupeFlux
                      </p>
                      <p className="text-md sm:text-lg font-bold text-gray-900">
                        {availableGroups.groupeFlux.length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-2 sm:p-4 rounded-lg shadow-sm border">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <Settings className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="ml-2 sm:ml-3">
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                        GroupeDalux
                      </p>
                      <p className="text-md sm:text-lg font-bold text-gray-900">
                        {availableGroups.groupeDalux.length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-2 sm:p-4 rounded-lg shadow-sm border">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Users className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="ml-2 sm:ml-3">
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                        Contacts
                      </p>
                      <p className="text-md sm:text-lg font-bold text-gray-900">
                        {contactsData?.objects?.length || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <h2 className="text-base font-semibold text-gray-900 flex-shrink-0">
                        Liste des Flux ({filteredAndSortedFlux.length})
                      </h2>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center rounded-lg p-0.5 bg-gray-100 border">
                          <button
                            onClick={() => setViewMode("all")}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                              viewMode === "all"
                                ? "bg-white text-blue-600 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            Vue Complète
                          </button>
                          <button
                            onClick={() => setViewMode("groupeFluxOnly")}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                              viewMode === "groupeFluxOnly"
                                ? "bg-white text-blue-600 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            Vue GroupeFlux
                          </button>
                        </div>
                        <div className="flex items-center rounded-lg p-0.5 bg-gray-100 border">
                          <button
                            onClick={() => setShowTags(!showTags)}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                              showTags
                                ? "bg-white text-blue-600 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                            title={
                              showTags
                                ? "Masquer les étiquettes"
                                : "Afficher les étiquettes"
                            }
                          >
                            Étiquettes
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative w-full sm:max-w-xs">
                        <Search
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                        />
                        <input
                          type="text"
                          placeholder="Rechercher un nom de flux..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full bg-gray-50 pl-9 pr-3 py-1.5 border border-gray-200 rounded-md text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                        />
                      </div>
                      <button
                        onClick={handleAddFlux}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md flex items-center justify-center gap-2 transition-colors font-medium shadow-sm text-xs flex-shrink-0"
                      >
                        <Plus size={14} />
                        <span>Nouveau Flux</span>
                      </button>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="relative w-full sm:max-w-xs">
                      <input
                        type="text"
                        placeholder="Ajouter des mots-clés (badges)..."
                        value={filterInput}
                        onChange={(e) => setFilterInput(e.target.value)}
                        onKeyDown={handleFilterKeyDown}
                        className="w-full bg-gray-50 pl-3 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                      />
                    </div>
                    {filterTags.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {filterTags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
                          >
                            {tag}
                            <button
                              onClick={() => removeFilterTag(tag)}
                              className="flex items-center justify-center w-4 h-4 rounded-full text-blue-500 hover:bg-blue-200 hover:text-blue-700"
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {fluxData.length === 0 && !contactsData?.objects?.length ? (
                  <div className="text-center py-12">
                    <div className="mb-6">
                      <div className="mx-auto w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center">
                        <Users size={32} className="text-purple-600" />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Commencez par importer vos contacts
                    </h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto text-sm">
                      Importez d'abord votre fichier Contacts pour avoir tous
                      les GroupeDalux disponibles, puis créez vos flux.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <SortableHeader
                            name="nom"
                            sortConfig={sortConfig}
                            onSort={requestSort}
                          >
                            Nom du Flux
                          </SortableHeader>
                          <SortableHeader
                            name="status"
                            sortConfig={sortConfig}
                            onSort={requestSort}
                          >
                            Statut
                          </SortableHeader>
                          {viewMode === "all" ? (
                            <>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Initiateur
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Analyse
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Autoriser
                              </th>
                            </>
                          ) : (
                            <>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Initiateur (Groupes Dalux)
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Analyse (Groupes Flux)
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Autoriser (Groupes Dalux)
                              </th>
                            </>
                          )}
                          <SortableHeader
                            name="createdAt"
                            sortConfig={sortConfig}
                            onSort={requestSort}
                          >
                            Créé le
                          </SortableHeader>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y-2 divide-gray-200">
                        {filteredAndSortedFlux.map((flux) => (
                          <tr key={flux.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 align-middle">
                              {viewMode === "groupeFluxOnly" ? (
                                <div className="flex items-center gap-3">
                                  <div className="text-sm font-bold text-gray-900">
                                    {flux.nom}
                                  </div>
                                  {showTags && (
                                    <div className="flex items-center gap-2">
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                          (flux.typePartage || "Publié") ===
                                          "Publié"
                                            ? "bg-green-100 text-green-800"
                                            : "bg-yellow-100 text-yellow-800"
                                        }`}
                                      >
                                        {flux.typePartage || "Publié"}
                                      </span>
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                          (flux.type || "Mandataire") ===
                                          "Mandataire"
                                            ? "bg-blue-100 text-blue-800"
                                            : "bg-purple-100 text-purple-800"
                                        }`}
                                      >
                                        {flux.type || "Mandataire"}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <div className="text-sm font-bold text-gray-900">
                                    {flux.nom}
                                  </div>
                                  {showTags && (
                                    <div className="flex items-center gap-2">
                                      <span
                                        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                                          (flux.typePartage || "Publié") ===
                                          "Publié"
                                            ? "bg-green-500 text-white"
                                            : "bg-yellow-500 text-white"
                                        }`}
                                      >
                                        {flux.typePartage || "Publié"}
                                      </span>
                                      <span
                                        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                                          (flux.type || "Mandataire") ===
                                          "Mandataire"
                                            ? "bg-blue-500 text-white"
                                            : "bg-purple-500 text-white"
                                        }`}
                                      >
                                        {flux.type || "Mandataire"}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>

                            <td className="px-4 py-3 align-middle whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="flex-grow min-w-[100px]">
                                  <StatusDropdown
                                    currentStatus={flux.status || "en attente"}
                                    onStatusChange={(newStatus) =>
                                      handleStatusChange(flux.id, newStatus)
                                    }
                                  />
                                </div>
                                <CommentDisplayTooltip comment={flux.comment}>
                                  <button
                                    onClick={(e) =>
                                      handleOpenCommentEditor(e, flux)
                                    }
                                    className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                                    title={
                                      flux.comment
                                        ? "Voir/Éditer le commentaire"
                                        : "Ajouter un commentaire"
                                    }
                                  >
                                    <MessageSquare
                                      size={16}
                                      className={
                                        flux.comment
                                          ? "text-blue-600"
                                          : "text-gray-400"
                                      }
                                    />
                                  </button>
                                </CommentDisplayTooltip>
                              </div>
                            </td>

                            {viewMode === "all" ? (
                              <>
                                <td className="px-4 py-3 align-middle">
                                  <div className="flex flex-wrap gap-1">
                                    {flux.initiateur?.standard &&
                                      flux.initiateur.standard.map(
                                        (dalux, idx) => (
                                          <div
                                            key={`std-${idx}`}
                                            className="inline-flex items-center px-2 py-1 text-xs rounded bg-blue-600 text-white"
                                          >
                                            {!isFluxGroup(dalux) && (
                                              <WarningTooltip text="Ce groupe ne contient pas le mot 'flux'. Il pourrait ne pas être approprié pour un flux de validation.">
                                                <AlertTriangle
                                                  size={12}
                                                  className="text-yellow-300 mr-1.5 cursor-help"
                                                />
                                              </WarningTooltip>
                                            )}
                                            <GroupeDaluxTooltip
                                              groupeName={dalux}
                                              contactsData={contactsData}
                                            >
                                              <span>{dalux}</span>
                                            </GroupeDaluxTooltip>
                                          </div>
                                        )
                                      )}
                                    {flux.initiateur?.observateur &&
                                      flux.initiateur.observateur.map(
                                        (dalux, idx) => (
                                          <div
                                            key={`obs-${idx}`}
                                            className="inline-flex items-center px-2 py-1 text-xs rounded bg-gray-500 text-white"
                                          >
                                            {!isFluxGroup(dalux) && (
                                              <WarningTooltip text="Ce groupe ne contient pas le mot 'flux'. Il pourrait ne pas être approprié pour un flux de validation.">
                                                <AlertTriangle
                                                  size={12}
                                                  className="text-yellow-300 mr-1.5 cursor-help"
                                                />
                                              </WarningTooltip>
                                            )}
                                            <GroupeDaluxTooltip
                                              groupeName={dalux}
                                              contactsData={contactsData}
                                            >
                                              <span>{dalux}</span>
                                            </GroupeDaluxTooltip>
                                          </div>
                                        )
                                      )}
                                    {(!flux.initiateur?.standard ||
                                      flux.initiateur.standard.length === 0) &&
                                      (!flux.initiateur?.observateur ||
                                        flux.initiateur.observateur.length ===
                                          0) && (
                                        <span className="text-xs text-gray-400 italic">
                                          Aucun initiateur
                                        </span>
                                      )}
                                  </div>
                                </td>

                                <td className="px-4 py-3 align-top">
                                  <div className="space-y-2">
                                    {flux.analyse &&
                                      flux.analyse.map((group, idx) => (
                                        <div key={idx} className="space-y-1">
                                          <div className="flex items-center gap-1.5 text-xs font-bold text-red-700">
                                            <Briefcase
                                              size={12}
                                              className="text-red-500 flex-shrink-0"
                                            />
                                            <span>{group.groupeFlux}</span>
                                          </div>
                                          <div className="flex flex-wrap gap-1">
                                            {group.groupeDalux &&
                                              group.groupeDalux.map(
                                                (dalux, dIdx) => (
                                                  <div
                                                    key={dIdx}
                                                    className="inline-flex items-center px-2 py-1 text-xs rounded bg-red-600 text-white"
                                                  >
                                                    {!isFluxGroup(dalux) && (
                                                      <WarningTooltip text="Ce groupe ne contient pas le mot 'flux'. Il pourrait ne pas être approprié pour un flux de validation.">
                                                        <AlertTriangle
                                                          size={12}
                                                          className="text-yellow-300 mr-1.5 cursor-help"
                                                        />
                                                      </WarningTooltip>
                                                    )}
                                                    <GroupeDaluxTooltip
                                                      groupeName={dalux}
                                                      contactsData={
                                                        contactsData
                                                      }
                                                    >
                                                      <span>{dalux}</span>
                                                    </GroupeDaluxTooltip>
                                                  </div>
                                                )
                                              )}
                                            {group.individuelPersons &&
                                              group.individuelPersons.length >
                                                0 && (
                                                <IndividuelTooltip
                                                  personnes={
                                                    group.individuelPersons
                                                  }
                                                >
                                                  <span className="inline-block px-2 py-1 text-xs rounded bg-gray-600 text-white cursor-help">
                                                    Individuel (
                                                    {
                                                      group.individuelPersons
                                                        .length
                                                    }
                                                    )
                                                  </span>
                                                </IndividuelTooltip>
                                              )}
                                          </div>
                                        </div>
                                      ))}
                                    {(!flux.analyse ||
                                      flux.analyse.length === 0) && (
                                      <span className="text-xs text-gray-400 italic">
                                        Aucune analyse
                                      </span>
                                    )}
                                  </div>
                                </td>

                                <td className="px-4 py-3 align-middle">
                                  <div className="flex flex-wrap gap-1">
                                    {flux.autoriser &&
                                      flux.autoriser.map((dalux, idx) => {
                                        // Déterminer le texte du rôle selon le type de partage
                                        const roleText =
                                          (flux.typePartage || "Publié") ===
                                          "Partagé"
                                            ? "Autoriser le partage de fichiers"
                                            : "Autoriser la publication de fichiers";

                                        return (
                                          <div
                                            key={idx}
                                            className="inline-flex items-center px-2 py-1 text-xs rounded bg-green-600 text-white"
                                            title={roleText}
                                          >
                                            {!isFluxGroup(dalux) && (
                                              <WarningTooltip text="Ce groupe ne contient pas le mot 'flux'. Il pourrait ne pas être approprié pour un flux de validation.">
                                                <AlertTriangle
                                                  size={12}
                                                  className="text-yellow-300 mr-1.5 cursor-help"
                                                />
                                              </WarningTooltip>
                                            )}
                                            <GroupeDaluxTooltip
                                              groupeName={dalux}
                                              contactsData={contactsData}
                                            >
                                              <span>{dalux}</span>
                                            </GroupeDaluxTooltip>
                                          </div>
                                        );
                                      })}
                                    {(!flux.autoriser ||
                                      flux.autoriser.length === 0) && (
                                      <span className="text-xs text-gray-400 italic">
                                        Aucune autorisation
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-3 align-middle">
                                  <div className="flex flex-wrap gap-1 items-center">
                                    {flux.initiateur?.standard &&
                                    flux.initiateur.standard.length > 0
                                      ? flux.initiateur.standard.map(
                                          (dalux, idx) => (
                                            <GroupeDaluxTooltip
                                              key={`std-${idx}`}
                                              groupeName={dalux}
                                              contactsData={contactsData}
                                            >
                                              <div className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-md border border-blue-200">
                                                {dalux}
                                              </div>
                                            </GroupeDaluxTooltip>
                                          )
                                        )
                                      : null}

                                    {flux.initiateur?.observateur &&
                                      flux.initiateur.observateur.length >
                                        0 && (
                                        <ObservateurTooltip
                                          groupes={flux.initiateur.observateur}
                                        >
                                          <div className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-200 rounded-md border border-gray-300">
                                            Observateur
                                          </div>
                                        </ObservateurTooltip>
                                      )}

                                    {(!flux.initiateur?.standard ||
                                      flux.initiateur.standard.length === 0) &&
                                      (!flux.initiateur?.observateur ||
                                        flux.initiateur.observateur.length ===
                                          0) && (
                                        <span className="text-xs text-gray-400 italic">
                                          Aucun
                                        </span>
                                      )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 align-top">
                                  <div className="flex flex-wrap gap-1">
                                    {flux.analyse && flux.analyse.length > 0 ? (
                                      flux.analyse.map((group, idx) => (
                                        <GroupeFluxTooltip
                                          key={idx}
                                          group={group}
                                          contactsData={contactsData}
                                        >
                                          <div className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-md border border-red-200">
                                            {group.groupeFlux}
                                          </div>
                                        </GroupeFluxTooltip>
                                      ))
                                    ) : (
                                      <span className="text-xs text-gray-400 italic">
                                        Aucun
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 align-middle">
                                  <div className="flex flex-wrap gap-1">
                                    {flux.autoriser &&
                                    flux.autoriser.length > 0 ? (
                                      flux.autoriser.map((dalux, idx) => (
                                        <GroupeDaluxTooltip
                                          key={idx}
                                          groupeName={dalux}
                                          contactsData={contactsData}
                                        >
                                          <div className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-md border border-green-200">
                                            {dalux}
                                          </div>
                                        </GroupeDaluxTooltip>
                                      ))
                                    ) : (
                                      <span className="text-xs text-gray-400 italic">
                                        Aucun
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </>
                            )}

                            <td className="px-4 py-3 align-middle text-xs text-gray-500">
                              {flux.createdAt
                                ? new Date(flux.createdAt).toLocaleDateString(
                                    "fr-FR"
                                  )
                                : "N/A"}
                            </td>

                            <td className="px-4 py-3 align-middle">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleEditFlux(flux)}
                                  className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                                  title="Éditer"
                                >
                                  <Edit2 size={16} />
                                </button>
                                <button
                                  onClick={() => handleCopyConfig(flux)}
                                  className="p-2 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-full transition-colors"
                                  title="Copier la configuration"
                                >
                                  <ClipboardCopy size={16} />
                                </button>
                                <button
                                  onClick={() => handleCopyFlux(flux)}
                                  className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-full transition-colors"
                                  title="Copier"
                                >
                                  <Copy size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteFlux(flux.id)}
                                  className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-colors"
                                  title="Supprimer"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Vue Export
                  </h2>
                  <p className="text-gray-600 mt-1 text-sm">
                    Données détaillées avec les personnes associées aux groupes
                  </p>
                </div>
                {fluxData.length > 0 && (
                  <button
                    onClick={() => {
                      console.log("Bouton export cliqué!");
                      handleExportFromView();
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md flex items-center justify-center gap-2 transition-colors font-medium shadow-sm text-xs"
                  >
                    <Save size={14} />
                    <span>Exporter CSV</span>
                  </button>
                )}
              </div>

              {fluxData.length > 0 ? (
                <div>
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Flux:</span>{" "}
                        {fluxData.length}
                      </div>
                      <div>
                        <span className="font-medium">GroupeDalux:</span>{" "}
                        {availableGroups.groupeDalux.length}
                      </div>
                      <div>
                        <span className="font-medium">Contacts:</span>{" "}
                        {contactsData?.objects?.length || 0}
                      </div>
                      <div>
                        <span className="font-medium">Lignes:</span>{" "}
                        {exportData.length}
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nom du flux
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date de création
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Statut
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Commentaire
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            N° Etape
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role Initiateur
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            GroupeFlux
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            GroupeDalux
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Personne
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type de partage
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {exportData.map((row, index) => (
                          <tr
                            key={index}
                            className={
                              index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            <td className="px-4 py-2 text-xs font-medium text-gray-900">
                              {row["Nom du flux"]}
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-500">
                              {row["Date de création"]}
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-500">
                              <span
                                className={`inline-flex items-center capitalize px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusStyles(
                                  row["Statut"]
                                )}`}
                              >
                                {row["Statut"]}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-500">
                              {row["Commentaire"]}
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-500">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  row["N° Etape"] === 1
                                    ? "bg-blue-100 text-blue-800"
                                    : row["N° Etape"] === 2
                                    ? "bg-orange-100 text-orange-800"
                                    : "bg-green-100 text-green-800"
                                }`}
                              >
                                {row["N° Etape"]}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-500">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  row["Role"] === "Initiateur"
                                    ? "bg-blue-100 text-blue-800"
                                    : row["Role"] === "Analyse"
                                    ? "bg-red-100 text-red-800"
                                    : row["Role"].includes("Autoriser") ||
                                      row["Role"] === "Autoriser"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {row["Role"] === "Autoriser" &&
                                row["N° Etape"] === 3
                                  ? row["Type de partage"] === "Partagé"
                                    ? "Autoriser le partage de fichiers"
                                    : "Autoriser la publication de fichiers"
                                  : row["Role"]}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-500">
                              {row["Role Initiateur"]}
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-500">
                              {row["GroupeFlux"] || "-"}
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-900">
                              {row["GroupeDalux"] && (
                                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                                  {row["GroupeDalux"]}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-900">
                              {row["Personne"] ? (
                                <div className="flex items-center">
                                  <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center mr-2">
                                    <Users
                                      size={12}
                                      className="text-gray-500"
                                    />
                                  </div>
                                  <span className="font-medium">
                                    {row["Personne"]}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-400 italic">
                                  Aucune personne assignée
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-500">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                                  row["Type de partage"] === "Publié"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-orange-100 text-orange-800"
                                }`}
                              >
                                {row["Type de partage"]}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-500">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                                  row["Type"] === "Mandataire"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-purple-100 text-purple-800"
                                }`}
                              >
                                {row["Type"]}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {exportData.length === 0 && (
                    <div className="text-center py-12">
                      <div className="mb-4">
                        <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                          <Users size={20} className="text-gray-400" />
                        </div>
                      </div>
                      <p className="text-gray-500">
                        Aucune donnée à afficher. Vérifiez vos flux et contacts.
                      </p>
                    </div>
                  )}

                  <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex justify-between items-center">
                      <div className="text-xs text-gray-600">
                        {exportData.length} lignes d'export générées
                      </div>
                      <button
                        onClick={() => {
                          console.log("Bouton télécharger cliqué!");
                          handleExportFromView();
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md flex items-center justify-center gap-2 transition-colors font-medium shadow-sm text-xs"
                      >
                        <Save size={14} />
                        <span>Télécharger CSV</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mb-6">
                    <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                      <Users size={24} className="text-gray-400" />
                    </div>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Aucun flux à exporter
                  </h3>
                  <p className="text-gray-500 mb-4 text-sm">
                    Créez d'abord quelques flux dans l'onglet "Vue Flux".
                  </p>
                  <div className="flex justify-center">
                    <button
                      onClick={() => setCurrentView("flux")}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm text-sm"
                    >
                      <Layers size={16} />
                      <span>Aller aux Flux</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {editingFlux && (
        <EditFluxModal
          flux={editingFlux}
          setEditingFlux={setEditingFlux}
          availableGroups={availableGroups}
          setAvailableGroups={setAvailableGroups}
          onSave={handleSaveFlux}
          onCancel={() => setEditingFlux(null)}
          removeGroupeFlux={removeGroupeFlux}
          addGroupeDalux={addGroupeDalux}
          removeGroupeDalux={removeGroupeDalux}
          contactsData={contactsData}
          addToast={addToast}
          onPasteConfig={handlePasteConfig}
          hasCopiedConfig={!!copiedConfig}
        />
      )}

      {editingComment && (
        <CommentEditor
          initialComment={editingComment.initialComment}
          position={editingComment.position}
          onSave={(comment) =>
            handleSaveComment(editingComment.fluxId, comment)
          }
          onCancel={() => setEditingComment(null)}
        />
      )}

      {showNewFluxModal && (
        <NewFluxModal
          onSave={handleSaveNewFlux}
          onCancel={() => setShowNewFluxModal(false)}
        />
      )}

      {copyingFlux && (
        <CopyFluxModal
          fluxToCopy={copyingFlux}
          onSave={handleSaveCopiedFlux}
          onCancel={() => setCopyingFlux(null)}
        />
      )}

      {showImportOptions && (
        <ImportOptionsModal
          stats={importStats}
          onReplace={() => handleReplaceImport()}
          onMerge={handleMergeImport}
          onCancel={() => setShowImportOptions(false)}
        />
      )}

      {showConflictResolution && (
        <ConflictResolutionModal
          conflicts={conflictingFlux}
          onResolve={handleConflictResolution}
          onCancel={() => setShowConflictResolution(false)}
        />
      )}
    </div>
  );
};

export default FluxAdminInterface;
